// HarmonogramHarok — harmonogram remesla ako hárok úsekov s Ganttom po dňoch (SPEC harmonogram v1, 2.4; v1.1 zahrnúť, m², plánované partie).
// Klávesnica hárka podľa 131.4. Klávesy sa zachytia na kontajneri a ďalej sa nepustia: inak šípky prepnú zákazku
// (useListKeyboard v ZakazkyView na window), Alt+N otvorí novú zákazku a Ctrl+Z zavolá globálne /api/undo/last.
// Späť hárka je lokálny zásobník; história na serveri je v Verziách.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { fetchAuth, fetchAuthJSON } from '../auth/fetchAuth.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { Btn, BtnNav, BtnGhost } from '../components/Btn.jsx'
import { Modal } from '../components/Modal.jsx'
import { useToast } from '../components/Toast.jsx'
import { useConfirm } from '../components/ConfirmDialog.jsx'
import { KpiCard, KpiRow, SearchableSelect, ListStatusBar } from '../components/Ui.jsx'
import StavStitok from '../components/StavStitok.jsx'
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { setDirty, isAnyDirty } from '../hooks/unsavedGuard.js'
import { useShortcuts } from '../hooks/useShortcuts.js'
import { usePersony, menoOsoby } from '../lib/persony.js'
import { COLORS, TYPO, SPACE, RADIUS, CELL, TINT, tint, norm, todayLocal, localISODate, MOD_KEY } from '../theme.js'
import {
  ZAHRNUT, ZAHRNUT_CELE, PLANOVANA, DNI_MAX, emptyHarok, normalizeHarok, newRiadok, nextSlotId, slotOf, computeRows, computeKpi,
  bytyUseku, spolocneUseku, objektyBezPoschodia, zahrnutOf, zahrnutText, nazovPomocnej, nazovPlanovanej, vykonSlotu, parseVykon, formatM2, formatCislo,
  parseDatum, parseDni, parseObjekty, parseTsv, formatDatum, formatDM, formatObjekty, poKey, poLabel,
  addCalDays, diffDays, mondayOf, isWeekendIso, dniText, usekyText, isEmptyRiadok, textNaFarbe,
} from '../utils/harmonogramHarok.js'

const WRITE_ROLES = ['admin', 'owner', 'office_manager', 'owner_view']
const DW = 22, LW = 230, RH = 38
// Gantt s viac dňami by pri preklepe v dátume vykreslil desaťtisíce prvkov
const GANTT_MAX_DNI = 800
const MESIACE = ['január', 'február', 'marec', 'apríl', 'máj', 'jún', 'júl', 'august', 'september', 'október', 'november', 'december']
const DOVODY = { migracia: 'Prevod pôvodného plánu', autoplan: 'Auto-naplánovanie', nahradenie_partie: 'Nahradenie partie', zmena_rezimu: 'Zmena režimu', pred_obnovou: 'Stav pred obnovením verzie', vlozenie_excel: 'Vloženie z Excelu' }
const VYMERY_REMESLA = { omietky: 'omietok', malby: 'malieb', potery: 'poterov' }
// Šírky sú zvolené tak, aby sa hárok zmestil do karty zákazky vedľa zoznamu; Stav a akcie sú navyše ukotvené vpravo.
const COLS = [
  { k: 'partia', t: 'Partia', w: 156, edit: 'select' },
  { k: 'usek', t: 'Úsek', w: 64, edit: 'text' },
  { k: 'zahrnut', t: 'Zahrnúť', w: 128, edit: 'toggle' },
  { k: 'objekty', t: 'Objekty', w: 72, edit: 'text' },
  { k: 'byty', t: 'Bytov', w: 76, num: true },
  { k: 'm2', t: 'm²', w: 70, num: true },
  { k: 'zaciatok', t: 'Začiatok', w: 138, edit: 'text' },
  { k: 'dni', t: 'Trvanie', w: 112, edit: 'text', num: true },
  { k: 'koniec', t: 'Koniec', w: 88 },
  { k: 'stav', t: 'Stav', w: 124 },
]
const AKCIE_W = 136
const COL_STAV = COLS.findIndex(c => c.k === 'stav')
const COL_USEK = COLS.findIndex(c => c.k === 'usek')
const CHYBA_BUNKY = { zaciatok: 'Začiatok zadaj v tvare 15.09.2026.', dni: `Dni zadaj ako číslo 1–${DNI_MAX}.`, objekty: 'Objekty zadaj ako A + B alebo „všetky“.' }
const bezAkcie = () => {}
// Prehľad skratiek (?) — klávesy spracuje kontajner hárka, preto sa v registri len zobrazia (enabled: false)
const SKRATKY_HARKA = [
  ['arrowdown', 'Pohyb po bunkách (šípky, Tab, Home, End)'], ['enter', 'Upraviť bunku (aj F2); Enter uloží a ide nižšie'],
  ['escape', 'Zrušiť úpravu'], ['delete', 'Vymazať obsah bunky'], ['f8', 'Hodnota z riadku vyššie'],
  ['b', 'Zahrnúť: prepnúť byty'], ['s', 'Zahrnúť: prepnúť spoločné priestory'], ['m', 'Trvanie: dni alebo z m²'],
  ['alt+n', 'Nový úsek pod označený riadok'], ['mod+z', 'Späť v hárku'], ['mod+y', 'Znova v hárku'], ['mod+v', 'Vložiť blok z Excelu'],
].map(([keys, label]) => ({ keys, label, scope: 'Harmonogram · hárok', handler: bezAkcie, preventDefault: false, options: { enabled: false } }))

const lbl = { fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, display: 'block' }
const inputBase = { padding: '6px 9px', border: `1px solid ${CELL.input.border}`, borderRadius: RADIUS.md, fontSize: TYPO.sizes.base, fontFamily: TYPO.family, color: COLORS.text, background: CELL.input.bg, outline: 'none', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }
const thS = { background: CELL.header.bg, color: CELL.header.text, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, textTransform: 'uppercase', letterSpacing: '0.03em', padding: '8px', borderBottom: `1px solid ${COLORS.line}`, whiteSpace: 'nowrap', textAlign: 'left' }
const kpiVal = { fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra, color: COLORS.text, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
const kpiSub = { fontSize: TYPO.sizes.xs, color: COLORS.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
const hintStyle = { fontSize: TYPO.sizes.xs, color: COLORS.muted, lineHeight: 1.4 }
const hatch = (a, b) => `repeating-linear-gradient(135deg, ${tint(COLORS.total, a)} 0 7px, ${tint(COLORS.total, b)} 7px 14px)`
const chip = (on, hl) => ({
  padding: '0 7px', height: 20, lineHeight: '18px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, fontFamily: 'inherit', borderRadius: RADIUS.sm,
  cursor: 'pointer', boxSizing: 'border-box', border: `1px solid ${on ? COLORS.nav : COLORS.line}`, background: on ? COLORS.navBg : COLORS.bg, color: on ? COLORS.nav : COLORS.muted,
  outline: hl ? `2px solid ${COLORS.nav}` : 'none', outlineOffset: 1,
})
const linkBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: COLORS.nav, textDecoration: 'underline', cursor: 'pointer' }

const sameValue = (a, b) => JSON.stringify(a == null ? '' : a) === JSON.stringify(b == null ? '' : b)
const withRow = (h, i, patch) => ({ ...h, riadky: h.riadky.map((r, j) => (j === i ? { ...r, ...patch } : r)) })
// Nepoužitý slot skutočnej partie bez výkonu sa zahodí; plánované partie a zadaný výkon ostanú, kým ich používateľ neodstráni.
const pruneSlots = (h) => {
  const used = new Set(h.riadky.map(r => r.slot).filter(Boolean))
  const zbytocny = (p) => !used.has(p.slot) && p.party_id && vykonSlotu(p) == null
  return h.partie.some(zbytocny) ? { ...h, partie: h.partie.filter(p => !zbytocny(p)) } : h
}
const partiaMeno = (p) => [p.nazov || p.party_id, p.partak_meno].filter(Boolean).join(' · ')
const bezPartaka = (p) => !!(p && Array.isArray(p.upozornenie) && p.upozornenie.includes('bez_partaka'))
const vykonText = (v) => formatCislo(v, v % 1 ? 1 : 0)
// trvanie z m² sa pri zápise dní prepne na dni (kto zadá počet dní, vie, koľko úsek trvá)
const patchBunky = (row, k, value) => (k === 'dni' && row.trvanie === 'm2' ? { dni: value, trvanie: 'dni' } : { [k]: value })
const zmena = (row, patch) => !Object.keys(patch).every(k => sameValue(row[k], patch[k]))
// Trvanie z m²: pri zápise sa do dni doplnia vypočítané dni — okno so starým kódom hárka pozná len dni a ukázalo by iný plán.
const dniPreZapis = (h, struktura, today) => {
  if (!h || !Array.isArray(h.riadky) || !h.riadky.some(r => r.trvanie === 'm2')) return h
  const rs = computeRows(h, today, struktura)
  let zmenene = false
  const riadky = h.riadky.map((r, i) => {
    if (r.trvanie !== 'm2' || !(rs[i] && rs[i].dni_efekt > 0) || r.dni === rs[i].dni_efekt) return r
    zmenene = true
    return { ...r, dni: rs[i].dni_efekt }
  })
  return zmenene ? { ...h, riadky } : h
}
// Výkon slotu; vymazaný výkon prepne úseky slotu s trvaním z m² na doterajšie dni, inak by stratili koniec aj ďalšie úseky partie.
const sVykonom = (h, slotId, nv, struktura, today) => {
  let riadky = h.riadky, prepnute = 0
  if (nv == null) {
    const rs = computeRows(h, today, struktura)
    riadky = h.riadky.map((r, i) => {
      if (r.slot !== slotId || r.trvanie !== 'm2') return r
      prepnute++
      return { ...r, trvanie: 'dni', dni: rs[i] && rs[i].dni_efekt > 0 ? rs[i].dni_efekt : r.dni }
    })
  }
  return { harok: { ...h, partie: h.partie.map(p => (p.slot === slotId ? { ...p, vykon_den: nv } : p)), riadky }, prepnute }
}

function flatPartie(p) {
  const out = []
  const push = (arr, skupina) => (Array.isArray(arr) ? arr : []).forEach(x => {
    if (x && x.party_id && !out.some(o => o.party_id === x.party_id)) out.push({ ...x, skupina_harok: skupina })
  })
  if (p) { push(p.na_zakazke, 'na_zakazke'); push(p.remesla, 'remesla'); push(p.dalsie, 'dalsie'); push(p.bez_partaka, 'bez_partaka') }
  return out
}
function slotMeno(slot, byId) {
  if (!slot) return ''
  if (slot.party_id) { const p = byId[slot.party_id]; return p ? partiaMeno(p) : (slot.nazov || slot.party_id) }
  return nazovPlanovanej(slot)
}
function slotFarba(slot, byId) {
  if (!slot) return COLORS.disabled
  if (!slot.party_id) return COLORS.total
  return (byId[slot.party_id] || {}).farba || slot.farba || COLORS.nav
}
// Skupiny ako neklikateľné riadky: SearchableSelect skupiny nepozná, preto hlavička = hodnota s predponou __h_
function partiaOptions(partie, harok, { planovane = true } = {}) {
  const o = []
  const seen = new Set()
  const uniq = (arr) => (Array.isArray(arr) ? arr : []).filter(x => x && x.party_id && !seen.has(x.party_id) && seen.add(x.party_id))
  const real = (x) => ({ value: 'party:' + x.party_id, label: partiaMeno(x) })
  const grp = (key, label, arr, map) => { if (!arr.length) return; o.push({ value: '__h_' + key, label }); arr.forEach(x => o.push(map(x))) }
  grp('na', 'NA ZÁKAZKE', uniq(partie && partie.na_zakazke), real)
  grp('rem', 'PARTIE REMESLA', uniq(partie && partie.remesla), real)
  grp('bez', 'BEZ PARŤÁKA', uniq(partie && partie.bez_partaka), x => ({ value: 'party:' + x.party_id, label: `${x.nazov || x.party_id} — bez parťáka` }))
  if (planovane) {
    grp('plan', 'PLÁNOVANÉ PARTIE', ((harok && harok.partie) || []).filter(s => !s.party_id), s => ({ value: 'slot:' + s.slot, label: nazovPlanovanej(s) + (s.pozn ? ' · ' + s.pozn : '') }))
    o.push({ value: '__new', label: 'Nová plánovaná partia…' })
  }
  grp('dal', 'ĎALŠIE PARTIE', uniq(partie && partie.dalsie), real)
  const mimo = ((harok && harok.partie) || []).filter(s => s.party_id && !seen.has(s.party_id))
  grp('mimo', 'V HÁRKU (MIMO PONUKY)', mimo, s => ({ value: 'party:' + s.party_id, label: s.nazov || s.party_id }))
  return o
}
// Enter na hlavičke skupiny (SearchableSelect zvýrazní prvú položku zoznamu) = prvá vyberateľná partia pod ňou, ktorá sedí na hľadaný text
function prvaVolbaPod(options, headerValue, hladane) {
  const q = norm(hladane || '').trim()
  const i = options.findIndex(o => o.value === headerValue)
  if (i < 0) return null
  const o = options.slice(i + 1).find(x => !x.value.startsWith('__h_') && (!q || norm(x.label).includes(q)))
  return o ? o.value : null
}

const casOf = (d) => {
  try { return new Intl.DateTimeFormat('sk-SK', { timeZone: 'Europe/Bratislava', hour: '2-digit', minute: '2-digit', hour12: false }).format(d) }
  catch (_) { return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') }
}
// saved_at zo SQLite je UTC bez zóny
const fmtCas = (s) => {
  if (!s) return ''
  const t = String(s).replace(' ', 'T')
  const d = new Date(/[zZ]$|[+-]\d\d:?\d\d$/.test(t) ? t : t + 'Z')
  return isNaN(d) ? String(s) : formatDatum(localISODate(d)) + ' ' + casOf(d)
}

function cellRaw(h, i, k) {
  const r = h && h.riadky[i]
  if (!r) return ''
  if (k === 'usek') return r.usek || ''
  if (k === 'objekty') return formatObjekty(r.objekty)
  if (k === 'zaciatok') return formatDatum(r.zaciatok)
  if (k === 'dni') return r.dni === '' || r.dni == null ? '' : String(r.dni)
  return ''
}
function parseCell(k, text, objekty, today) {
  if (k === 'usek') return { ok: true, value: String(text || '').trim() }
  if (k === 'zaciatok') { const v = parseDatum(text, today); return v === null ? { ok: false } : { ok: true, value: v } }
  if (k === 'dni') { const v = parseDni(text); return v === null ? { ok: false } : { ok: true, value: v } }
  if (k === 'objekty') {
    if (!String(text || '').trim()) return { ok: true, value: [] }
    const p = parseObjekty(text, objekty)
    return p.chyba ? { ok: false } : { ok: true, value: p.objekty }
  }
  return { ok: false }
}

export default function HarmonogramHarok({ projectId, remeslo }) {
  const toast = useToast()
  const confirm = useConfirm()
  const { user } = useAuth()
  const { meno } = usePersony()
  const today = todayLocal()
  const pid = encodeURIComponent(projectId)
  const rem = encodeURIComponent(remeslo)
  const getUrl = `/api/harmonogram/${pid}?remeslo=${rem}`
  const dirtyKey = `harmonogram:harok:${projectId}:${remeslo}`
  // klient=harok-v11: server rozozná zápis tohto kódu od starého okna hárka
  const putBase = `/api/harmonogram/${pid}?remeslo=${rem}&cast=harok&klient=harok-v11`

  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState('')
  const [serverOk, setServerOk] = useState(true)
  const [harok, setHarokState] = useState(null)
  const [struktura, setStruktura] = useState({ zdroj_remeslo: '', objekty: [], poschodia: [] })
  const [partie, setPartie] = useState(null)
  const [tempo, setTempo] = useState(null)   // potery_rekap_v1: skutocne tempo zo Vymer (iba zobrazenie)
  const [saveState, setSaveState] = useState({ kod: 'ok', cas: '' })   // ok | pending | saving | error
  const [conflict, setConflict] = useState(null)
  const [cur, setCur] = useState({ r: 0, c: 0 })
  const [edit, setEdit] = useState(null)       // {rowId, k, r, c, opt} rozpísaná bunka; opt = zvýraznená voľba Zahrnúť
  const [draft, setDraftState] = useState('')
  const [bad, setBad] = useState({})           // `${rowId}|${k}` → neplatný rozpísaný text
  const [gridFocus, setGridFocus] = useState(false)
  const [terminDraft, setTerminDraft] = useState(null)
  const [terminBad, setTerminBad] = useState(false)
  const [partieOkno, setPartieOkno] = useState(null)   // {fokus, pole, nahradit: {slot, party}}
  const [pDraft, setPDraft] = useState({})             // `${slot}|nazov` / `${slot}|vykon` → rozpísaný text v okne Partie a výkon
  const [pBad, setPBad] = useState({})
  const [verzie, setVerzie] = useState(null)       // {loading, list, err}
  const [pdfBusy, setPdfBusy] = useState(false)    // p41: PDF hárku na hlavičkovom papieri
  const [odoslat, setOdoslat] = useState(null)     // p41: okno Odoslať harmonogram {email, cc, predmet, text, sms, busy}
  const [vlozit, setVlozit] = useState(null)       // {rows, at}

  const canWrite = WRITE_ROLES.includes(user && user.role)
  const canEdit = canWrite && serverOk && !conflict && !loadErr

  const aliveRef = useRef(true)
  const harokRef = useRef(null)
  const revRef = useRef(null)
  const serverJsonRef = useRef(null)
  const undoRef = useRef([])
  const redoRef = useRef([])
  const timerRef = useRef(null)
  const pendingRef = useRef(null)    // {base, dovod} čakajúci zápis
  const chainRef = useRef(Promise.resolve())
  const seqRef = useRef(0)
  const savedSeqRef = useRef(0)
  const conflictRef = useRef(null)
  const editRef = useRef(null)
  const gridRef = useRef(null)
  const rootRef = useRef(null)
  const selWrapRef = useRef(null)
  const focusWantedRef = useRef(false)   // true = fokus na bunku; 'tichy' = bez posunu stránky (po dialógu)
  const lastKeyRef = useRef('')
  const hlasenyRef = useRef(false)       // posledný stav nahlásený do unsavedGuard
  const pFokusRef = useRef('')
  const strukturaRef = useRef(struktura)
  strukturaRef.current = struktura

  useShortcuts(SKRATKY_HARKA)

  const setHarok = (next) => { harokRef.current = next; setHarokState(next) }
  const setDraft = (v) => { setDraftState(v); if (editRef.current) editRef.current.draft = v }
  // po dialógu, toaste alebo modale fokus späť do hárka — inak Ctrl+Z z body zavolá globálne /api/undo/last
  const vratFokus = () => { focusWantedRef.current = 'tichy'; setCur(c => ({ ...c })) }
  const dialogRef = useRef(false)
  const potvrd = async (opts) => { dialogRef.current = true; try { return await confirm(opts) } finally { dialogRef.current = false } }
  const modalOtvorenyRef = useRef(false)
  modalOtvorenyRef.current = !!(verzie || partieOkno || vlozit)
  // Kým je otvorený dialóg hárka, Ctrl+Z/Y mimo poľa nič nerobí (fokus je v dialógu alebo na body, mimo kontajnera)
  useEffect(() => {
    const h = (e) => {
      if (!dialogRef.current && !modalOtvorenyRef.current) return
      if (!(e.ctrlKey || e.metaKey) || (e.code !== 'KeyZ' && e.code !== 'KeyY')) return
      const tag = e.target && e.target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target && e.target.isContentEditable)) return
      e.preventDefault()
      e.stopPropagation()
    }
    document.addEventListener('keydown', h, true)
    return () => document.removeEventListener('keydown', h, true)
  }, [])
  // Esc v dialógu hárka zavrie dialóg (Modal počúva na document), ďalej na window ísť nesmie: useListKeyboard v ZakazkyView
  // by zrušil výber zákazky a karta by prešla na /zakazky/null
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && (dialogRef.current || modalOtvorenyRef.current)) e.stopPropagation() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [])

  // ── načítanie ──
  const load = useCallback((silent, force) => {
    if (!silent) setLoading(true)
    const seq0 = seqRef.current
    return fetchAuthJSON(getUrl).then(d => {
      if (!aliveRef.current) return
      // tiché obnovenie: počas behu GET pribudla lokálna zmena, vlastný zápis alebo úprava bunky → odpoveď je zastaraná
      if (silent && !force && (seqRef.current !== seq0 || seqRef.current !== savedSeqRef.current || pendingRef.current || editRef.current || conflictRef.current ||
        (d && d.rev != null && revRef.current != null && d.rev < revRef.current))) return
      // starý server otázku remesla nepozná a zápis by skončil v pláne omietok — vtedy len čítanie
      setServerOk(!!d && d.remeslo === remeslo)
      revRef.current = d && d.rev != null ? d.rev : null
      const s = (d && d.struktura) || {}
      setStruktura({ zdroj_remeslo: s.zdroj_remeslo || '', objekty: Array.isArray(s.objekty) ? s.objekty.map(String) : [], poschodia: Array.isArray(s.poschodia) ? s.poschodia : [] })
      setPartie(d && d.partie ? d.partie : null)
      setTempo(d && d.tempo ? d.tempo : null)
      const raw = JSON.stringify(d && d.harok ? d.harok : null)
      if (force || !harokRef.current || raw !== serverJsonRef.current) {
        const remote = !!harokRef.current
        serverJsonRef.current = raw
        setHarok(d && d.harok ? normalizeHarok(d.harok) : emptyHarok())
        if (remote) { undoRef.current = []; redoRef.current = [] }
        savedSeqRef.current = seqRef.current
        setBad({})
      }
      setLoadErr('')
    }).catch(e => {
      if (!aliveRef.current || silent) return
      setLoadErr(e.message)
      toast.error('Harmonogram sa nepodarilo načítať: ' + e.message)
    }).finally(() => { if (aliveRef.current && !silent) setLoading(false) })
  }, [getUrl, remeslo])

  useEffect(() => { load(false) }, [load])

  // ── ukladanie: debounce 800 ms, zápisy za sebou, base_rev z poslednej odpovede ──
  const sendNow = useCallback(() => {
    const job = pendingRef.current
    if (!job) return chainRef.current
    pendingRef.current = null
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    chainRef.current = chainRef.current.catch(() => {}).then(async () => {
      if (conflictRef.current) return
      const seq = seqRef.current
      const body = dniPreZapis(harokRef.current, strukturaRef.current, todayLocal())
      const json = JSON.stringify(body)
      const put = () => fetchAuth(job.base + (revRef.current != null ? '&base_rev=' + revRef.current : '') + (job.dovod ? '&dovod=' + encodeURIComponent(job.dovod) : ''), { method: 'PUT', body: json })
      if (aliveRef.current) setSaveState(s => ({ ...s, kod: 'saving' }))
      let r = await put()
      if (r.status === 409) {
        // rev je spoločný pre výkon aj hárok remesla: zápis výkonu (aj zo starého okna) zvýši rev, hoci sa hárok nezmenil.
        // Ak je hárok na serveri stále ten, z ktorého vychádzame, zápis sa raz zopakuje s novým rev.
        let g = null
        try { g = await fetchAuthJSON(getUrl) } catch (_) {}
        if (g && g.rev != null && JSON.stringify(g.harok ? g.harok : null) === serverJsonRef.current) {
          revRef.current = g.rev
          r = await put()
        }
      }
      if (r.status === 409) {
        let d = {}
        try { d = await r.json() } catch (_) {}
        conflictRef.current = { kto: d.updated_by || '', kedy: d.updated_at || '' }
        if (aliveRef.current) { setConflict(conflictRef.current); setSaveState(s => ({ ...s, kod: 'error' })) }
        toast.warn(`Harmonogram medzitým zmenil ${menoOsoby(d.updated_by) || 'iný používateľ'}. Načítať znova`, { duration: 10000 })
        return
      }
      if (!r.ok) {
        let det = ''
        try { const j = await r.json(); det = j.error || '' } catch (_) {}
        throw new Error('HTTP ' + r.status + (det ? ': ' + det : ''))
      }
      let d = {}
      try { d = await r.json() } catch (_) {}
      if (d && d.rev != null) revRef.current = d.rev
      serverJsonRef.current = json
      if (seq > savedSeqRef.current) savedSeqRef.current = seq
      if (aliveRef.current) setSaveState({ kod: seqRef.current > savedSeqRef.current ? 'pending' : 'ok', cas: casOf(new Date()) })
    }).catch(e => {
      if (!pendingRef.current) pendingRef.current = { base: job.base, dovod: job.dovod }
      if (aliveRef.current) setSaveState(s => ({ ...s, kod: 'error' }))
      toast.error('Uloženie harmonogramu zlyhalo: ' + e.message)
    })
    return chainRef.current
  }, [])

  const scheduleSave = (dovod) => {
    const base = putBase
    const prev = pendingRef.current
    pendingRef.current = { base, dovod: dovod || (prev && prev.dovod) || '' }
    setSaveState(s => ({ ...s, kod: 'pending' }))
    if (timerRef.current) clearTimeout(timerRef.current)
    if (dovod) sendNow()
    else timerRef.current = setTimeout(() => { timerRef.current = null; sendNow() }, 800)
  }

  const apply = (next, opts = {}) => {
    if (!canEdit || !harokRef.current) return
    undoRef.current.push(harokRef.current)
    if (undoRef.current.length > 100) undoRef.current.shift()
    redoRef.current = []
    seqRef.current++
    setHarok(next)
    scheduleSave(opts.dovod)
  }
  const undo = () => {
    if (!canEdit) return
    const prev = undoRef.current.pop()
    if (!prev) { toast.info('V hárku niet čo vrátiť.'); return }
    redoRef.current.push(harokRef.current)
    seqRef.current++
    setHarok(prev)
    scheduleSave()
  }
  const redo = () => {
    if (!canEdit) return
    const next = redoRef.current.pop()
    if (!next) { toast.info('V hárku niet čo zopakovať.'); return }
    undoRef.current.push(harokRef.current)
    seqRef.current++
    setHarok(next)
    scheduleSave()
  }

  // rozpísaná platná bunka sa zapíše aj pri odchode zo záložky alebo remesla
  const commitEditSilently = () => {
    const ed = editRef.current, h = harokRef.current
    if (!ed || !h || !canEdit) return
    const i = h.riadky.findIndex(r => r.id === ed.rowId)
    if (i < 0 || COLS[ed.c].edit !== 'text' || ed.draft === ed.orig) return
    const res = parseCell(ed.k, ed.draft, struktura.objekty, today)
    if (!res.ok) return
    const patch = patchBunky(h.riadky[i], ed.k, res.value)
    if (!zmena(h.riadky[i], patch)) return
    editRef.current = null
    undoRef.current.push(h)
    seqRef.current++
    harokRef.current = withRow(h, i, patch)
    if (!pendingRef.current) pendingRef.current = { base: putBase, dovod: '' }
  }
  // rozpísaný platný názov alebo výkon v okne Partie a výkon sa zapíše aj pri odchode zo záložky alebo zákazky
  const commitPDraftSilently = () => {
    const h0 = harokRef.current
    if (!h0 || !canEdit || !Object.keys(pDraft).length) return
    let h = h0
    for (const [key, text] of Object.entries(pDraft)) {
      const slotId = key.slice(0, key.lastIndexOf('|'))
      const s = slotOf(h, slotId)
      if (!s) continue
      if (key.endsWith('|nazov')) {
        const nm = String(text).replace(/\s+/g, ' ').trim()
        if (s.party_id || !nm || nm === s.nazov || h.partie.some(p => p.slot !== slotId && !p.party_id && norm(nazovPlanovanej(p)).trim() === norm(nm).trim())) continue
        h = { ...h, partie: h.partie.map(p => (p.slot === slotId ? { ...p, nazov: nm } : p)) }
      } else {
        const v = parseVykon(text)
        const nv = v === '' ? null : v
        if (v === null || vykonSlotu(s) === nv) continue
        h = sVykonom(h, slotId, nv, struktura, today).harok
      }
    }
    if (h === h0) return
    undoRef.current.push(h0)
    seqRef.current++
    harokRef.current = h
    if (!pendingRef.current) pendingRef.current = { base: putBase, dovod: '' }
  }
  const flush = async () => {
    commitEditSilently()
    commitPDraftSilently()
    if (seqRef.current > savedSeqRef.current && !pendingRef.current) pendingRef.current = { base: putBase, dovod: '' }
    await sendNow()
    if (seqRef.current > savedSeqRef.current) throw new Error('Harmonogram sa neuložil')
  }
  const flushRef = useRef(flush)
  flushRef.current = flush

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      if (timerRef.current) clearTimeout(timerRef.current)
      // Zahodiť v dialógu neuložených zmien vyprázdni unsavedGuard — vtedy sa nič neukladá
      if (hlasenyRef.current && !isAnyDirty()) { pendingRef.current = null; editRef.current = null; return }
      flushRef.current().then(() => setDirty(dirtyKey, false)).catch(() => { if (conflictRef.current) setDirty(dirtyKey, false) })
    }
  }, [])

  // chyby buniek len pre riadky, ktoré v hárku ešte sú (po Späť, odstránení alebo načítaní)
  const liveBad = useMemo(() => {
    const ids = new Set(harok ? harok.riadky.map(r => r.id) : [])
    const o = {}
    for (const [k, v] of Object.entries(bad)) if (ids.has(k.slice(0, k.lastIndexOf('|')))) o[k] = v
    return o
  }, [bad, harok])
  const nBad = Object.keys(liveBad).length

  // Automaticky uložiteľné zmeny (čakajúci zápis) sa pri odchode odošlú; do unsavedGuard idú len tie, ktoré sa uložiť nedajú.
  const editChanged = !!edit && editRef.current && COLS[edit.c].edit === 'text' && editRef.current.draft !== editRef.current.orig
  const isDirty = saveState.kod === 'error' || !!conflict || nBad > 0 || editChanged || terminDraft !== null || Object.keys(pDraft).length > 0
  useEffect(() => { hlasenyRef.current = isDirty; setDirty(dirtyKey, isDirty, () => flushRef.current()) }, [isDirty, dirtyKey])

  useLiveRefresh(() => { load(true) }, () =>
    !edit && !partieOkno && !verzie && !vlozit && !conflictRef.current && !pendingRef.current &&
    seqRef.current === savedSeqRef.current && nBad === 0 && terminDraft === null)

  // ── odvodené ──
  const neplatne = useMemo(() => new Set(Object.keys(liveBad).map(k => k.slice(0, k.lastIndexOf('|')))), [liveBad])
  const rows = useMemo(() => (harok ? computeRows(harok, today, struktura, { neplatne }) : []), [harok, today, struktura, neplatne])
  const kpi = useMemo(() => (harok ? computeKpi(harok, rows, today) : null), [harok, rows, today])
  // p41 (Mirko 17. 9. 2026 „tlačidlo na generovanie a odoslanie harmonogramu“): PDF a odoslanie len z uloženého plánu
  const neulozene = !harok || saveState.kod !== 'ok' || !!conflict || revRef.current == null
  const pdfTelo = () => ({
    rev: revRef.current, dnes: today, kpi,
    rows: (harok ? harok.riadky : []).map((r, i) => { const c = rows[i] || {}; return { id: r.id, start: c.start || null, end: c.end || null, dni: c.dni || 0, m2: c.m2 == null ? null : c.m2, stav: c.stav ? c.stav.kod : '' } }),
  })
  const stiahniPdf = async () => {
    if (pdfBusy || neulozene) return
    setPdfBusy(true)
    try {
      const r = await fetchAuth(`/api/harmonogram/${pid}/pdf?remeslo=${rem}`, { method: 'POST', body: JSON.stringify(pdfTelo()) })
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'HTTP ' + r.status) }
      const blob = await r.blob()
      const m = /filename="([^"]+)"/.exec(r.headers.get('Content-Disposition') || '')
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = m ? m[1] : `Harmonogram_${remeslo}_${projectId}.pdf`
      document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove() }, 5000)
    } catch (e) { toast.error('PDF sa nevytvorilo: ' + e.message) } finally { setPdfBusy(false) }
  }
  const otvorOdoslat = async () => {
    if (neulozene) return
    let nazov = projectId, email = ''
    try {
      const p = await fetchAuthJSON('/api/projects/' + encodeURIComponent(projectId)); const pr = p && p.project ? p.project : p
      nazov = (pr && (pr.nazov_stavby_zod || pr.name)) || projectId
      if (pr && pr.customerId) { const c = await fetchAuthJSON('/api/customers/' + encodeURIComponent(pr.customerId)); const cu = c && c.customer ? c.customer : c; email = (cu && cu.email) || '' }
    } catch (_) {}
    const rm = { omietky: 'omietok', malby: 'malieb', potery: 'poterov' }[remeslo] || remeslo
    const termin = harok && harok.termin ? ' Termín ukončenia: ' + harok.termin.split('-').reverse().map((x, i) => i < 2 ? String(+x) : x).join('. ') + '.' : ''
    setOdoslat({ email, cc: '', sms: false, busy: false, predmet: 'Harmonogram ' + rm + ' — ' + nazov,
      text: 'Dobrý deň,\n\nv prílohe posielame aktuálny harmonogram ' + rm + ' pre stavbu ' + nazov + '.' + termin + '\n\nS pozdravom\nKAMIR s.r.o.' })
  }
  const odoslatHarmonogram = async () => {
    if (!odoslat || odoslat.busy) return
    setOdoslat(o => ({ ...o, busy: true }))
    try {
      const r = await fetchAuth(`/api/harmonogram/${pid}/odoslat?remeslo=${rem}`, { method: 'POST', body: JSON.stringify({ ...pdfTelo(), email: odoslat.email, cc: odoslat.cc, predmet: odoslat.predmet, text: odoslat.text, sms: !!odoslat.sms }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || 'HTTP ' + r.status)
      toast.success('Harmonogram odoslaný na ' + d.to + (d.sms && d.sms.ok ? ' · SMS ' + d.sms.to : ''))
      setOdoslat(null); vratFokus()
    } catch (e) { toast.error(e.message); setOdoslat(o => (o ? { ...o, busy: false } : o)) }
  }
  const byId = useMemo(() => { const m = {}; flatPartie(partie).forEach(p => { m[p.party_id] = p }); return m }, [partie])
  const optsPartia = useMemo(() => partiaOptions(partie, harok), [partie, harok])
  const optsNahradit = useMemo(() => partiaOptions(partie, null, { planovane: false }), [partie])
  // návrhy poschodí: nadzemné zhora (8. NP … 1. NP), potom podzemné zhora (1. PP, 2. PP …)
  const poschodiaNavrhy = useMemo(() => {
    const keys = []
    for (const p of struktura.poschodia) { const k = poKey(p.po); if (k && !keys.includes(k)) keys.push(k) }
    return keys.sort((a, b) => {
      const pa = a.endsWith('PP'), pb = b.endsWith('PP')
      return (pa - pb) || (pa ? parseInt(a, 10) - parseInt(b, 10) : parseInt(b, 10) - parseInt(a, 10))
    }).map(poLabel)
  }, [struktura])
  const zdrojText = struktura.zdroj_remeslo ? 'z Výmer ' + (VYMERY_REMESLA[struktura.zdroj_remeslo] || struktura.zdroj_remeslo) : ''

  useEffect(() => {
    if (harok && cur.r > harok.riadky.length - 1) setCur(p => ({ ...p, r: Math.max(0, harok.riadky.length - 1) }))
  }, [harok])

  useEffect(() => {
    if (edit || !focusWantedRef.current || !gridRef.current) return
    const tichy = focusWantedRef.current === 'tichy'
    focusWantedRef.current = false
    const el = gridRef.current.querySelector(`[data-cell="${cur.r}-${cur.c}"]`)
    if (!el) return
    el.focus({ preventScroll: tichy })
    // focus() nepočíta s ukotvenými stĺpcami Stav a akcie vpravo — aktívna bunka by ostala pod nimi (hranica = ľavý okraj bunky Stav v riadku)
    const box = gridRef.current, cr = el.getBoundingClientRect(), br = box.getBoundingClientRect()
    const stavEl = cur.c !== COL_STAV && el.parentElement ? el.parentElement.querySelector(`[data-cell="${cur.r}-${COL_STAV}"]`) : null
    const vpravo = stavEl ? stavEl.getBoundingClientRect().left : br.right
    if (cr.right > vpravo) box.scrollLeft += Math.ceil(cr.right - vpravo)
    else if (cr.left < br.left) box.scrollLeft -= Math.ceil(br.left - cr.left)
  })

  useEffect(() => {
    if (!edit || COLS[edit.c].edit !== 'select' || !selWrapRef.current) return
    const inp = selWrapRef.current.querySelector('input')
    if (!inp) return
    inp.focus()
    // písmeno napísané v Pohybe začne hľadanie (SearchableSelect pri fokuse hľadanie vynuluje, preto až po fokuse)
    const seed = editRef.current && editRef.current.seedSelect
    if (seed) {
      editRef.current.seedSelect = null
      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set
      setter.call(inp, seed)
      inp.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }, [edit])

  // ── pohyb a úpravy buniek ──
  const nRows = harok ? harok.riadky.length : 0
  const moveTo = (r, c) => {
    focusWantedRef.current = true
    setCur({ r: Math.max(0, Math.min(nRows - 1, r)), c: Math.max(0, Math.min(COLS.length - 1, c)) })
  }
  // false = hranica hárka (Tab má z hárka odísť)
  const tabMove = (back) => {
    let r = cur.r, c = cur.c
    for (let k = 0; k < COLS.length * 2; k++) {
      c += back ? -1 : 1
      if (c >= COLS.length) { if (r >= nRows - 1) return false; r++; c = 0 }
      if (c < 0) { if (r <= 0) return false; r--; c = COLS.length - 1 }
      if (COLS[c].edit) break
    }
    moveTo(r, c)
    return true
  }

  const startEdit = (seed, r = cur.r, c = cur.c) => {
    const h = harokRef.current
    const col = COLS[c], row = h && h.riadky[r]
    if (!canEdit || !col || !col.edit || !row) return
    const key = row.id + '|' + col.k
    // trvanie z m²: úprava začne prázdna a placeholder ukáže vypočítané dni (uložené dni sú z posledného zápisu)
    const orig = bad[key] != null ? bad[key] : (col.k === 'dni' && row.trvanie === 'm2' ? '' : cellRaw(h, r, col.k))
    const isSelect = col.edit === 'select'
    const d = seed != null && !isSelect ? seed : orig
    editRef.current = {
      rowId: row.id, k: col.k, r, c, draft: d, orig: bad[key] != null ? null : orig, seedSelect: isSelect ? seed : null,
      zahrnut: col.edit === 'toggle' ? zahrnutOf(row) : null, undoLen: undoRef.current.length,
    }
    setDraftState(d)
    setCur({ r, c })
    setEdit({ rowId: row.id, k: col.k, r, c, opt: 0 })
  }
  const finishEdit = (after, refocus = true) => {
    editRef.current = null
    setEdit(null)
    setDraftState('')
    if (refocus) focusWantedRef.current = true
    if (after) after()
  }
  const cancelEdit = (after, refocus = true) => finishEdit(after, refocus)
  const clearBad = (key) => setBad(b => { if (!(key in b)) return b; const n = { ...b }; delete n[key]; return n })

  const commitEdit = (after, fromBlur = false) => {
    const ed = editRef.current, h = harokRef.current
    if (!ed || !h) return true
    const i = h.riadky.findIndex(r => r.id === ed.rowId)
    if (i < 0) { finishEdit(after, !fromBlur); return true }
    const key = ed.rowId + '|' + ed.k
    const row = h.riadky[i]
    // trvanie z m²: nezmenený text dní režim neprepne
    if (ed.k === 'dni' && row.trvanie === 'm2' && ed.draft === ed.orig) { finishEdit(after, !fromBlur); return true }
    const res = parseCell(ed.k, ed.draft, struktura.objekty, today)
    if (!res.ok) {
      setBad(b => ({ ...b, [key]: ed.draft }))
      toast.warn(CHYBA_BUNKY[ed.k] || 'Neplatná hodnota.')
      if (fromBlur) finishEdit(null, false)
      return false
    }
    clearBad(key)
    const patch = patchBunky(row, ed.k, res.value)
    if (zmena(row, patch)) apply(withRow(h, i, patch))
    finishEdit(after, !fromBlur)
    return true
  }

  const pickPartia = (val, i) => {
    const h = harokRef.current
    if (!h || !h.riadky[i] || !val || val.startsWith('__h_')) return false
    let partieArr = h.partie, slotId = '', novy = false
    if (val === '__new') {
      slotId = nextSlotId(h)
      novy = true
      partieArr = [...h.partie, { slot: slotId, party_id: null, nazov: nazovPomocnej(h), pozn: '', farba: COLORS.total, vykon_den: null }]
    } else if (val.startsWith('slot:')) {
      slotId = val.slice(5)
      if (!slotOf(h, slotId)) return false
    } else if (val.startsWith('party:')) {
      const id = val.slice(6)
      const p = byId[id] || {}
      const ex = h.partie.find(s => s.party_id === id)
      if (ex) slotId = ex.slot
      else {
        slotId = nextSlotId(h)
        partieArr = [...h.partie, { slot: slotId, party_id: id, nazov: p.nazov || id, pozn: '', farba: p.farba || COLORS.nav, vykon_den: null }]
      }
      if (bezPartaka(p)) toast.info(`${p.nazov || id} je bez parťáka — doplň parťáka v Zamestnancoch.`)
    } else return false
    if (h.riadky[i].slot === slotId && partieArr === h.partie) return true
    apply(pruneSlots(withRow({ ...h, partie: partieArr }, i, { slot: slotId })))
    return novy ? { novy: slotId } : true
  }

  const toggleZahrnut = (i, v) => {
    const h = harokRef.current, row = h && h.riadky[i]
    if (!canEdit || !row) return
    const z = zahrnutOf(row)
    const next = z.includes(v) ? z.filter(x => x !== v) : ZAHRNUT_CELE.filter(x => x === v || z.includes(x))
    if (!next.length) { toast.warn('Zahrnúť musí mať aspoň jednu voľbu: Byty alebo Spoločné.'); return }
    focusWantedRef.current = 'tichy'
    apply(withRow(h, i, { zahrnut: next }))
  }
  // Esc v úprave Zahrnúť vráti voľby spred úpravy (prepnutia sa ukladajú hneď) a zahodí ich kroky Späť
  const vratZahrnut = () => {
    const ed = editRef.current, h = harokRef.current
    if (!ed || !ed.zahrnut || !h || !canEdit) return
    const i = h.riadky.findIndex(r => r.id === ed.rowId)
    if (i < 0 || sameValue(zahrnutOf(h.riadky[i]), ed.zahrnut)) return
    undoRef.current.length = Math.min(undoRef.current.length, ed.undoLen)
    redoRef.current = []
    seqRef.current++
    setHarok(withRow(h, i, { zahrnut: ed.zahrnut }))
    scheduleSave()
  }
  const setTrvanie = (i, mode) => {
    const h = harokRef.current, row = h && h.riadky[i]
    if (!canEdit || !row || (row.trvanie === 'm2' ? 'm2' : 'dni') === mode) return
    const patch = { trvanie: mode }
    // prepnutie na dni prevezme zobrazené vypočítané dni, aby sa plán nerozpadol
    if (mode === 'dni' && rows[i] && rows[i].dni_efekt > 0) patch.dni = rows[i].dni_efekt
    // partia bez výkonu: z m² by úsek nemal koniec a ďalšie úseky partie by stratili dátumy — hneď sa ponúkne výkon (návrh z dní úseku)
    const slot = slotOf(h, row.slot)
    const ponukniVykon = mode === 'm2' && !!slot && vykonSlotu(slot) == null
    if (!ponukniVykon) focusWantedRef.current = 'tichy'
    apply(withRow(h, i, patch))
    if (ponukniVykon) otvorVykon(slot.slot, rows[i] && rows[i].vykon_odhad, row.usek)
  }

  const clearCell = () => {
    const col = COLS[cur.c], h = harokRef.current, row = h && h.riadky[cur.r]
    if (!canEdit || !col || !col.edit || !row) return
    clearBad(row.id + '|' + col.k)
    const patch = { partia: { slot: '' }, usek: { usek: '' }, zahrnut: { zahrnut: ZAHRNUT_CELE.slice() }, objekty: { objekty: [] }, zaciatok: { zaciatok: '' }, dni: row.trvanie === 'm2' ? null : { dni: '' } }[col.k]
    if (!patch || !zmena(row, patch)) return
    const next = withRow(h, cur.r, patch)
    apply(col.k === 'partia' ? pruneSlots(next) : next)
  }
  const copyFromAbove = () => {
    const col = COLS[cur.c], h = harokRef.current
    if (!canEdit || !col || !col.edit || !h || cur.r < 1) return
    const above = h.riadky[cur.r - 1], row = h.riadky[cur.r]
    clearBad(row.id + '|' + col.k)
    const patch = col.k === 'partia' ? { slot: above.slot }
      : col.k === 'zahrnut' ? { zahrnut: zahrnutOf(above) }
        : col.k === 'dni' ? { dni: above.dni, trvanie: above.trvanie === 'm2' ? 'm2' : 'dni' }
          : { [col.k]: above[col.k] }
    if (!zmena(row, patch)) return
    const next = withRow(h, cur.r, patch)
    apply(col.k === 'partia' ? pruneSlots(next) : next)
  }
  const bytyText = (row) => {
    const z = zahrnutOf(row), b = bytyUseku(row, struktura), s = spolocneUseku(row, struktura)
    const bOk = z.includes('byty') && b != null, sOk = z.includes('spolocne') && s != null
    const parts = []
    // poschodie bez bytov: „2 spol.“, nie „0 + 2 spol.“
    if (bOk && (b > 0 || !sOk || !(s > 0))) parts.push(String(b))
    if (sOk && (s > 0 || !parts.length)) parts.push(`${s} spol.`)
    return parts.join(' + ')
  }
  const cellText = (i, k) => {
    const h = harokRef.current, row = h && h.riadky[i], c = rows[i]
    if (!row || !c) return ''
    if (k === 'partia') return slotMeno(slotOf(h, row.slot), byId)
    if (k === 'zahrnut') return zahrnutText(zahrnutOf(row))
    if (k === 'zaciatok') return formatDatum(c.start)
    if (k === 'koniec') return formatDatum(c.end)
    if (k === 'byty') return bytyText(row)
    if (k === 'm2') return c.m2 == null ? '' : formatM2(c.m2)
    if (k === 'dni') return row.trvanie === 'm2' ? (c.dni_efekt ? String(c.dni_efekt) : '') : cellRaw(h, i, k)
    if (k === 'stav') return c.stav.text
    return cellRaw(h, i, k)
  }

  const addRow = () => {
    const h = harokRef.current
    if (!canEdit || !h) return
    if (editRef.current && COLS[editRef.current.c].edit === 'text' && !commitEdit()) return
    const at = gridFocus ? Math.min(cur.r, h.riadky.length - 1) : h.riadky.length - 1
    const ref = h.riadky[at]
    const row = newRiadok({
      slot: ref ? ref.slot : '',
      objekty: ref && ref.objekty.length ? ref.objekty : struktura.objekty.slice(), dni: ref ? ref.dni : '', trvanie: ref && ref.trvanie === 'm2' ? 'm2' : 'dni',
    })
    const riadky = [...h.riadky]
    riadky.splice(at + 1, 0, row)
    const next = { ...h, riadky }
    apply(next)
    editRef.current = { rowId: row.id, k: 'usek', r: at + 1, c: COL_USEK, draft: '', orig: '' }
    setDraftState('')
    setCur({ r: at + 1, c: COL_USEK })
    setEdit({ rowId: row.id, k: 'usek', r: at + 1, c: COL_USEK, opt: 0 })
  }

  const removeRow = async (i) => {
    const h0 = harokRef.current, row = h0 && h0.riadky[i]
    if (!canEdit || !row) return
    const nazov = row.usek ? `„${row.usek}“` : 'bez názvu'
    const ok = await potvrd({ title: 'Odstrániť úsek', message: `Odstrániť úsek ${nazov} z harmonogramu? Späť ho vrátiš tlačidlom Späť alebo ${MOD_KEY}+Z.`, danger: true, confirmLabel: 'Odstrániť' })
    vratFokus()
    if (!ok) return
    const h = harokRef.current
    const idx = h.riadky.findIndex(r => r.id === row.id)
    if (idx < 0) return
    const slot = slotOf(h, row.slot)
    let riadky = h.riadky.filter(r => r.id !== row.id)
    if (!riadky.length) riadky = [newRiadok()]
    apply(pruneSlots({ ...h, riadky }))
    toast.undo(`Úsek ${nazov} odstránený`, () => {
      const h2 = harokRef.current
      if (!h2 || h2.riadky.some(r => r.id === row.id)) return
      let back = row, partie2 = h2.partie
      if (slot && !h2.partie.some(s => s.slot === slot.slot)) partie2 = [...h2.partie, slot]
      else if (slot && !h2.partie.some(s => s.slot === slot.slot && s.party_id === slot.party_id)) {
        const ns = nextSlotId(h2)
        partie2 = [...h2.partie, { ...slot, slot: ns }]
        back = { ...row, slot: ns }
      }
      const r2 = [...h2.riadky]
      r2.splice(Math.min(idx, r2.length), 0, back)
      apply({ ...h2, partie: partie2, riadky: r2 })
      vratFokus()
    })
  }
  const toggleHotovo = (i) => {
    const h = harokRef.current
    if (!canEdit || !h || !h.riadky[i]) return
    apply(withRow(h, i, { hotovo: !h.riadky[i].hotovo }))
  }

  const commitTermin = () => {
    if (terminDraft === null) return
    const v = parseDatum(terminDraft, today)
    if (v === null) { setTerminBad(true); toast.warn('Termín zadaj v tvare 31.10.2026.'); return }
    setTerminBad(false)
    setTerminDraft(null)
    const h = harokRef.current
    if (h && v !== h.termin) apply({ ...h, termin: v })
  }
  const setRezimDni = (dni) => { const h = harokRef.current; if (h && h.dni !== dni) apply({ ...h, dni }) }

  // ── klávesnica ──
  const onKeyCapture = (e) => {
    lastKeyRef.current = e.key
    const mod = e.ctrlKey || e.metaKey
    const stop = () => { e.preventDefault(); e.stopPropagation() }
    if (e.altKey && !mod && e.code === 'KeyN') { stop(); addRow(); return }
    const t = e.target, tag = t && t.tagName
    const inField = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!(t && t.isContentEditable)
    if (mod && (e.code === 'KeyZ' || e.code === 'KeyY')) {
      if (inField) { e.stopPropagation(); return }
      stop()
      if (e.code === 'KeyY' || e.shiftKey) redo(); else undo()
      return
    }
    // klik do KPI, Ganttu alebo textu dá fokus kontajneru — šípky vrátia kurzor do hárka
    if (harok && t === rootRef.current && e.key.startsWith('Arrow')) { stop(); moveTo(cur.r, cur.c); return }
    if (!harok || !gridRef.current || !gridRef.current.contains(t)) return

    if (edit) {
      const typ = COLS[edit.c].edit
      if (typ === 'toggle') {
        if (mod) return
        stop()
        const { r, c } = edit
        const k = e.key.toLowerCase()
        if (k === 'b' || k === 's') toggleZahrnut(r, k === 'b' ? 'byty' : 'spolocne')
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') setEdit(ed => (ed ? { ...ed, opt: e.key === 'ArrowLeft' ? 0 : 1 } : ed))
        else if (e.key === ' ') toggleZahrnut(r, ZAHRNUT[edit.opt || 0].value)
        else if (e.key === 'Enter') finishEdit(() => moveTo(r + 1, c))
        else if (e.key === 'Escape') { vratZahrnut(); cancelEdit() }
        else if (e.key === 'Tab') { const back = e.shiftKey; cancelEdit(() => tabMove(back)) }
        else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') finishEdit(() => moveTo(r + (e.key === 'ArrowUp' ? -1 : 1), c))
        else if (e.key === 'F8') { finishEdit(); copyFromAbove() }
        return
      }
      const isSelect = typ === 'select'
      if (isSelect && e.key === 'Enter') lastKeyRef.current = 'Enter:vyber'
      if (e.key === 'Escape') { stop(); cancelEdit(); return }
      if (e.key === 'Tab') { stop(); const back = e.shiftKey; if (isSelect) cancelEdit(() => tabMove(back)); else commitEdit(() => tabMove(back)); return }
      if (e.key === 'F8') {
        stop()
        if (edit.r < 1) return
        if (isSelect) { finishEdit(); copyFromAbove() } else setDraft(cellRaw(harokRef.current, edit.r - 1, edit.k))
        return
      }
      if (!isSelect && e.key === 'Enter') { stop(); const { r, c } = edit; commitEdit(() => moveTo(r + 1, c)); return }
      return
    }

    if (mod && e.code === 'KeyC') {
      const sel = window.getSelection && window.getSelection()
      if (sel && !sel.isCollapsed) return
      stop()
      try { navigator.clipboard.writeText(cellText(cur.r, COLS[cur.c].k)).catch(() => {}) } catch (_) {}
      return
    }
    switch (e.key) {
      case 'ArrowDown': stop(); moveTo(cur.r + 1, cur.c); return
      case 'ArrowUp': stop(); moveTo(cur.r - 1, cur.c); return
      case 'ArrowRight': stop(); moveTo(cur.r, cur.c + 1); return
      case 'ArrowLeft': stop(); moveTo(cur.r, cur.c - 1); return
      case 'PageDown': stop(); moveTo(cur.r + 10, cur.c); return
      case 'PageUp': stop(); moveTo(cur.r - 10, cur.c); return
      case 'Home': stop(); moveTo(mod ? 0 : cur.r, 0); return
      case 'End': stop(); moveTo(mod ? nRows - 1 : cur.r, COLS.length - 1); return
      case 'Tab': if (tabMove(e.shiftKey)) stop(); return
      case 'Enter': case 'F2':
        stop()
        if (canEdit && COLS[cur.c].edit) startEdit(null)
        else if (e.key === 'Enter') moveTo(cur.r + 1, cur.c)
        return
      // Esc opustí bunku, fokus ostane na kontajneri hárka: Ctrl+Z ostane lokálne a šípky vrátia kurzor do hárka (z body by prepli zákazku)
      case 'Escape': stop(); if (rootRef.current) rootRef.current.focus({ preventScroll: true }); return
      case 'Delete': case 'Backspace': stop(); clearCell(); return
      case 'F8': stop(); copyFromAbove(); return
      default: break
    }
    if (!mod && !e.altKey && COLS[cur.c].edit === 'toggle') {
      const k = e.key.toLowerCase()
      if (k === 'b' || k === 's') { stop(); toggleZahrnut(cur.r, k === 'b' ? 'byty' : 'spolocne'); return }
      if (e.key === ' ') { stop(); startEdit(null); return }
    }
    if (!mod && !e.altKey && COLS[cur.c].k === 'dni' && e.key.toLowerCase() === 'm') {
      stop()
      const row = harokRef.current && harokRef.current.riadky[cur.r]
      if (row) setTrvanie(cur.r, row.trvanie === 'm2' ? 'dni' : 'm2')
      return
    }
    if (e.key === '?' && COLS[cur.c].edit !== 'text') return
    if (e.key.length === 1 && !mod && !e.altKey) {
      stop()
      if (canEdit && (COLS[cur.c].edit === 'text' || COLS[cur.c].edit === 'select')) startEdit(e.key)
    }
  }
  const onKeyBubble = (e) => {
    const mod = e.ctrlKey || e.metaKey
    const tag = e.target && e.target.tagName
    // „?“ mimo textovej bunky a poľa otvorí prehľad skratiek
    if (e.key === '?' && !edit && tag !== 'INPUT' && tag !== 'TEXTAREA' && COLS[cur.c] && COLS[cur.c].edit !== 'text') return
    if (!mod || e.code === 'KeyZ' || e.code === 'KeyY') e.stopPropagation()
  }

  const onPaste = (e) => {
    if (!canEdit || !harok || !gridRef.current || !gridRef.current.contains(e.target)) return
    const text = (e.clipboardData && e.clipboardData.getData('text')) || ''
    const lines = text.replace(/\r/g, '').split('\n').filter(l => l.trim())
    const multi = text.includes('\t') || lines.length > 1
    if (!multi) {
      if (edit || !lines.length) return
      const col = COLS[cur.c]
      if (!col.edit || col.edit !== 'text') return
      e.preventDefault()
      const h = harokRef.current, row = h.riadky[cur.r]
      const res = parseCell(col.k, lines[0].trim(), struktura.objekty, today)
      const key = row.id + '|' + col.k
      if (!res.ok) { setBad(b => ({ ...b, [key]: lines[0].trim() })); toast.warn(CHYBA_BUNKY[col.k] || 'Neplatná hodnota.'); return }
      clearBad(key)
      const patch = patchBunky(row, col.k, res.value)
      if (zmena(row, patch)) apply(withRow(h, cur.r, patch))
      return
    }
    e.preventDefault()
    e.stopPropagation()
    const list = flatPartie(partie).map(p => ({ key: 'party:' + p.party_id, party_id: p.party_id, nazov: p.nazov, partak_meno: p.partak_meno, farba: p.farba, upozornenie: p.upozornenie }))
    harok.partie.filter(s => !s.party_id).forEach(s => list.push({ key: 'slot:' + s.slot, party_id: null, nazov: nazovPlanovanej(s) }))
    const parsed = parseTsv(text, list, struktura, today)
    if (!parsed.length) return
    const rowsN = parsed.map(x => (x.planovana ? { ...x, nazov_vlozenia: x.planovana_nazov || PLANOVANA } : x))
    if (edit) cancelEdit(null, false)
    setVlozit({ rows: rowsN, at: cur.r })
  }

  const vlozitPotvrdit = () => {
    const h = harokRef.current
    if (!vlozit || !h) return
    const partieArr = [...h.partie]
    const badNew = {}
    const nove = vlozit.rows.map(x => {
      let slotId = ''
      if (x.partia && x.partia.party_id) {
        const ex = partieArr.find(s => s.party_id === x.partia.party_id)
        if (ex) slotId = ex.slot
        else { slotId = nextSlotId({ partie: partieArr }); partieArr.push({ slot: slotId, party_id: x.partia.party_id, nazov: x.partia.nazov || x.partia.party_id, pozn: '', farba: x.partia.farba || COLORS.nav, vykon_den: null }) }
      } else if (x.partia && String(x.partia.key || '').startsWith('slot:') && partieArr.some(s => s.slot === x.partia.key.slice(5))) {
        slotId = x.partia.key.slice(5)
      } else if (x.planovana) {
        const nm = x.nazov_vlozenia || PLANOVANA
        const ex = partieArr.find(s => !s.party_id && norm(nazovPlanovanej(s)).trim() === norm(nm).trim())
        if (ex) slotId = ex.slot
        else { slotId = nextSlotId({ partie: partieArr }); partieArr.push({ slot: slotId, party_id: null, nazov: nm, pozn: '', farba: COLORS.total, vykon_den: null }) }
      }
      const r = newRiadok({ slot: slotId, usek: x.usek, zahrnut: x.zahrnut, objekty: x.objekty, zaciatok: x.zaciatok, dni: x.dni })
      if (x.zaciatok_chyba) badNew[r.id + '|zaciatok'] = x.zaciatok_text
      if (x.dni_chyba) badNew[r.id + '|dni'] = x.dni_text
      if (x.objekty_chyba) badNew[r.id + '|objekty'] = x.objekty_text
      return r
    })
    const at = Math.max(0, Math.min(vlozit.at, h.riadky.length - 1))
    const riadky = [...h.riadky]
    const nahrad = riadky[at] && isEmptyRiadok(riadky[at])
    riadky.splice(nahrad ? at : at + 1, nahrad ? 1 : 0, ...nove)
    apply({ ...h, partie: partieArr, riadky }, { dovod: 'vlozenie_excel' })
    if (Object.keys(badNew).length) setBad(b => ({ ...b, ...badNew }))
    setVlozit(null)
    focusWantedRef.current = true
    setCur({ r: nahrad ? at : at + 1, c: 0 })
    toast.success(`Vložené z Excelu: ${nove.length} ${nove.length === 1 ? 'riadok' : (nove.length < 5 ? 'riadky' : 'riadkov')}. Späť cez ${MOD_KEY}+Z.`)
  }

  // ── Partie a výkon ──
  const otvorPartie = (opts) => {
    const { draft: d0, ...o } = opts || {}
    setPDraft(d0 || {}); setPBad({})
    pFokusRef.current = ''
    setPartieOkno({ fokus: '', pole: 'nazov', nahradit: null, navrh: null, ...o })
  }
  // okno Partie a výkon s fokusom na výkon slotu; návrh (m²/deň z dní úseku) je predvyplnený a označený
  const otvorVykon = (slotId, navrh, usek) => {
    const text = navrh > 0 ? String(navrh) : ''
    otvorPartie({ fokus: slotId, pole: 'vykon', navrh: text ? { slot: slotId, text, usek: usek || '' } : null, draft: text ? { [slotId + '|vykon']: text } : null })
  }
  const dropPDraft = (key) => {
    setPDraft(d => { if (!(key in d)) return d; const n = { ...d }; delete n[key]; return n })
    setPBad(b => { if (!(key in b)) return b; const n = { ...b }; delete n[key]; return n })
  }
  // vráti false pri neplatnom texte (okno sa nezavrie)
  const commitSlotNazov = (slotId, text) => {
    const key = slotId + '|nazov'
    const h = harokRef.current, s = slotOf(h, slotId)
    if (text === undefined) { dropPDraft(key); return true }
    const nm = String(text).replace(/\s+/g, ' ').trim()
    if (!h || !s || s.party_id) { dropPDraft(key); return true }
    if (!nm) { setPBad(b => ({ ...b, [key]: true })); toast.warn('Názov plánovanej partie nesmie byť prázdny.'); return false }
    if (h.partie.some(p => p.slot !== slotId && !p.party_id && norm(nazovPlanovanej(p)).trim() === norm(nm).trim())) {
      setPBad(b => ({ ...b, [key]: true })); toast.warn(`Plánovaná partia „${nm}“ už v hárku je.`); return false
    }
    dropPDraft(key)
    if (nm !== s.nazov) apply({ ...h, partie: h.partie.map(p => (p.slot === slotId ? { ...p, nazov: nm } : p)) })
    return true
  }
  const commitSlotVykon = (slotId, text) => {
    const key = slotId + '|vykon'
    const h = harokRef.current, s = slotOf(h, slotId)
    if (text === undefined || !h || !s) { dropPDraft(key); return true }
    const v = parseVykon(text)
    if (v === null) { setPBad(b => ({ ...b, [key]: true })); toast.warn('Výkon zadaj ako kladné číslo m² za deň, napríklad 180.'); return false }
    dropPDraft(key)
    const nv = v === '' ? null : v
    if (vykonSlotu(s) === nv) return true
    const { harok: next, prepnute } = sVykonom(h, slotId, nv, struktura, today)
    apply(next)
    if (prepnute) toast.info(`Výkon vymazaný: ${prepnute} ${usekyText(prepnute)} s trvaním z m² ${prepnute >= 2 && prepnute <= 4 ? 'ostávajú' : 'ostáva'} s doterajšími dňami.`)
    return true
  }
  const zavrietPartie = () => {
    let ok = true
    for (const [key, text] of Object.entries(pDraft)) {
      const slotId = key.slice(0, key.lastIndexOf('|'))
      ok = (key.endsWith('|nazov') ? commitSlotNazov(slotId, text) : commitSlotVykon(slotId, text)) && ok
    }
    if (!ok) return
    setPartieOkno(null)
    vratFokus()
  }
  const pridatPlanovanu = () => {
    const h = harokRef.current
    if (!canEdit || !h) return
    const slotId = nextSlotId(h)
    apply({ ...h, partie: [...h.partie, { slot: slotId, party_id: null, nazov: nazovPomocnej(h), pozn: '', farba: COLORS.total, vykon_den: null }] })
    pFokusRef.current = ''
    setPartieOkno(o => ({ ...(o || {}), fokus: slotId, pole: 'nazov' }))
  }
  const odstranitSlot = (slotId) => {
    const h = harokRef.current
    if (!canEdit || !h || h.riadky.some(r => r.slot === slotId)) return
    dropPDraft(slotId + '|nazov'); dropPDraft(slotId + '|vykon')
    apply({ ...h, partie: h.partie.filter(p => p.slot !== slotId) })
  }

  const nahraditPotvrdit = async () => {
    const h0 = harokRef.current
    const nah = partieOkno && partieOkno.nahradit
    if (!nah || !nah.slot || !String(nah.party || '').startsWith('party:') || !h0) return
    const id = nah.party.slice(6)
    const p = byId[id]
    const slot = slotOf(h0, nah.slot)
    if (!p || !slot) return
    const pocet = h0.riadky.filter(r => r.slot === slot.slot).length
    const stare = slotMeno(slot, byId)
    const hotovo = (text) => {
      setPartieOkno(o => (o ? { ...o, nahradit: null } : o))
      toast.success(text)
    }
    // partia už má v hárku iný slot: dva sloty tej istej partie by sa reťazili zvlášť a prekrývali
    const inySlot = h0.partie.find(s => s.slot !== slot.slot && s.party_id === id)
    if (inySlot) {
      const n = h0.riadky.filter(r => r.slot === inySlot.slot).length
      const vZ = vykonSlotu(slot), vC = vykonSlotu(inySlot)
      const vykonInfo = vZ != null && vC != null && vZ !== vC ? ` Ostane výkon ${vykonText(vC)} m²/deň partie ${partiaMeno(p)}, výkon ${vykonText(vZ)} m²/deň partie ${stare} sa zahodí.` : ''
      const ok = await potvrd({ title: 'Zlúčiť do jednej partie', message: `${partiaMeno(p)} už má v hárku ${n} ${usekyText(n)}. Zlúčiť úseky do jedného poradia? Úseky ostanú v poradí tabuľky a prázdny začiatok bude pokračovať po predchádzajúcom úseku tejto partie. Bez zlúčenia sa partia nenahradí.${vykonInfo}`, confirmLabel: 'Zlúčiť' })
      if (!ok) return
      const h = harokRef.current
      const zdroj = h && slotOf(h, slot.slot), ciel = h && slotOf(h, inySlot.slot)
      if (!zdroj || !ciel) return
      const vykon = vykonSlotu(ciel) != null ? vykonSlotu(ciel) : vykonSlotu(zdroj)
      apply({
        ...h,
        partie: h.partie.filter(s => s.slot !== zdroj.slot).map(s => (s.slot === ciel.slot ? { ...s, vykon_den: vykon } : s)),
        riadky: h.riadky.map(r => (r.slot === zdroj.slot ? { ...r, slot: ciel.slot } : r)),
      }, { dovod: 'nahradenie_partie' })
      hotovo(`${stare} zlúčená s partiou ${partiaMeno(p)} (${pocet + n} ${usekyText(pocet + n)}).`)
      return
    }
    apply({ ...h0, partie: h0.partie.map(s => (s.slot === slot.slot ? { ...s, party_id: id, nazov: p.nazov || id, farba: p.farba || s.farba } : s)) }, { dovod: 'nahradenie_partie' })
    if (bezPartaka(p)) toast.info(`${p.nazov || id} je bez parťáka — doplň parťáka v Zamestnancoch.`)
    const v0 = vykonSlotu(slot)
    hotovo(`${stare} nahradená partiou ${partiaMeno(p)} (${pocet} ${usekyText(pocet)}).${slot.party_id && v0 != null ? ` Výkon ${vykonText(v0)} m²/deň ostal pôvodný — ak má nová partia iný, zmeň ho v tomto okne.` : ''}`)
  }

  const openVerzie = async () => {
    setVerzie({ loading: true, list: [] })
    try {
      const list = await fetchAuthJSON(`/api/harmonogram/${pid}/verzie?remeslo=${rem}&cast=harok`)
      if (aliveRef.current) setVerzie({ loading: false, list: Array.isArray(list) ? list : [] })
    } catch (e) {
      if (aliveRef.current) setVerzie({ loading: false, list: [], err: e.message })
    }
  }
  const obnovit = async (v) => {
    const ok = await potvrd({ title: 'Obnoviť verziu', message: `Obnoviť hárok do stavu pred uložením ${fmtCas(v.saved_at)}${v.saved_by ? ' (' + meno(v.saved_by) + ')' : ''}? Terajší stav sa uloží medzi verzie.`, confirmLabel: 'Obnoviť' })
    if (!ok) return
    try {
      await flush()
      // base_rev: zmenu, ktorú medzitým uložil niekto iný, obnova neprepíše bez hlásenia
      const r = await fetchAuth(`/api/harmonogram/${pid}/verzie/${encodeURIComponent(v.id)}/obnovit${revRef.current != null ? '?base_rev=' + revRef.current : ''}`, { method: 'POST', body: '{}' })
      if (r.status === 409) {
        let d = {}
        try { d = await r.json() } catch (_) {}
        conflictRef.current = { kto: d.updated_by || '', kedy: d.updated_at || '' }
        setConflict(conflictRef.current)
        setSaveState(s => ({ ...s, kod: 'error' }))
        setVerzie(null)
        vratFokus()
        toast.warn(`Harmonogram medzitým zmenil ${menoOsoby(d.updated_by) || 'iný používateľ'}. Načítaj ho znova a obnovu zopakuj.`, { duration: 10000 })
        return
      }
      if (!r.ok) { let det = ''; try { const j = await r.json(); det = j.error || '' } catch (_) {} throw new Error('HTTP ' + r.status + (det ? ': ' + det : '')) }
      setVerzie(null)
      undoRef.current = []; redoRef.current = []
      setBad({})
      await load(true, true)
      vratFokus()
      toast.success('Verzia obnovená.')
    } catch (e) { toast.error('Obnovenie zlyhalo: ' + e.message) }
  }

  const nacitatZnova = async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    pendingRef.current = null
    conflictRef.current = null
    setConflict(null)
    undoRef.current = []; redoRef.current = []
    setBad({})
    if (edit) cancelEdit(null, false)
    await load(true, true)
    setSaveState({ kod: 'ok', cas: '' })
    vratFokus()
  }

  // ── render ──
  if (loading) return <div style={{ padding: SPACE.xl, color: COLORS.muted }}>Načítavam harmonogram…</div>
  if (!harok) return <div style={{ padding: SPACE.xl, color: COLORS.remove }}>Harmonogram sa nepodarilo načítať{loadErr ? ': ' + loadErr : ''}.</div>

  const saveText = { pending: 'Neuložené zmeny…', saving: 'Ukladám…', error: 'Neuložené' }[saveState.kod] || (saveState.cas ? 'Uložené ' + saveState.cas : '')
  const rowNow = harok.riadky[cur.r]
  const termin = harok.termin
  const rez = kpi.rezerva_dni
  const vykonZa = harok.dni === 'prac' ? 'pracovný deň' : 'kalendárny deň'

  // nepriehľadné pozadie ukotvených buniek (zebra je polopriehľadná a pod ňou by presvital posunutý obsah)
  const plneBg = (i) => (i % 2 ? `linear-gradient(${CELL.zebra.bg}, ${CELL.zebra.bg}), ${COLORS.bg}` : COLORS.bg)
  const hlavickaBg = `linear-gradient(${CELL.header.bg}, ${CELL.header.bg}), ${COLORS.bg}`

  const renderCell = (row, i, col, ci) => {
    const c = rows[i]
    const key = row.id + '|' + col.k
    const badText = liveBad[key]
    const active = cur.r === i && cur.c === ci
    const editing = !!edit && edit.rowId === row.id && edit.k === col.k
    const inputEdit = editing && col.edit !== 'toggle'
    const style = {
      padding: inputEdit ? '3px 4px' : '4px 8px', height: 34, borderBottom: `1px solid ${COLORS.lineSoft}`, verticalAlign: 'middle',
      whiteSpace: 'nowrap', textAlign: col.num ? 'right' : 'left', fontVariantNumeric: 'tabular-nums', cursor: 'default',
      outline: active && (gridFocus || editing) ? `2px solid ${COLORS.nav}` : 'none', outlineOffset: -2,
      background: col.edit && canEdit ? CELL.input.bg : undefined,
    }
    if (col.k === 'stav') Object.assign(style, { position: 'sticky', right: AKCIE_W, zIndex: 1, background: plneBg(i), boxShadow: `inset 1px 0 0 ${COLORS.line}`, maxWidth: col.w, overflow: 'hidden' })
    if (badText != null && !editing) { style.background = COLORS.removeBg; style.color = COLORS.remove; style.boxShadow = `inset 0 0 0 1px ${COLORS.remove}` }
    let content = null
    let title
    if (editing && col.edit === 'select') {
      content = (
        <div ref={selWrapRef} style={{ minWidth: col.w - 10 }}
          onBlur={e => { if (selWrapRef.current && selWrapRef.current.contains(e.relatedTarget)) return; if (editRef.current) cancelEdit(null, false) }}>
          <SearchableSelect value={(() => { const s = slotOf(harok, row.slot); return s ? (s.party_id ? 'party:' + s.party_id : 'slot:' + s.slot) : '' })()}
            options={optsPartia} menuPortal placeholder="Hľadať partiu…"
            onChange={v => {
              const enter = lastKeyRef.current === 'Enter:vyber'
              let val = v
              if (String(v).startsWith('__h_')) {
                if (!enter) return
                const inp = selWrapRef.current && selWrapRef.current.querySelector('input')
                val = prvaVolbaPod(optsPartia, v, inp ? inp.value : '')
                if (!val) return
              }
              const res = pickPartia(val, i)
              if (!res) return
              if (res.novy) { finishEdit(null, false); otvorPartie({ fokus: res.novy, pole: 'nazov' }) }
              else finishEdit(enter ? () => moveTo(i + 1, ci) : null)
            }} />
        </div>
      )
    } else if (inputEdit) {
      const listId = `harm-po-${projectId}-${remeslo}`
      content = (
        <div>
          <input value={draft}
            ref={el => { if (el && document.activeElement !== el) { el.focus(); const L = el.value.length; try { el.setSelectionRange(L, L) } catch (_) {} } }}
            onChange={e => setDraft(e.target.value)}
            onBlur={() => { if (editRef.current && editRef.current.rowId === row.id && editRef.current.k === col.k) commitEdit(null, true) }}
            list={col.k === 'usek' && poschodiaNavrhy.length ? listId : undefined}
            placeholder={col.k === 'zaciatok' ? (c.auto ? formatDatum(c.start) : 'dd.mm.rrrr') : (col.k === 'dni' && row.trvanie === 'm2' && c.dni_efekt ? String(c.dni_efekt) : '')}
            aria-label={col.k === 'dni' ? 'Trvanie v dňoch' : col.t}
            style={{ ...inputBase, width: '100%', padding: '4px 6px', textAlign: col.num ? 'right' : 'left', borderColor: badText != null ? COLORS.remove : CELL.input.border, background: badText != null ? COLORS.removeBg : CELL.input.bg }} />
          {col.k === 'usek' && poschodiaNavrhy.length > 0 && (
            <datalist id={listId}>{poschodiaNavrhy.map(p => <option key={p} value={p} />)}</datalist>
          )}
          {col.k === 'objekty' && struktura.objekty.length > 0 && (() => {
            const sel = parseObjekty(draft, struktura.objekty).objekty
            const vsetky = sel.length === struktura.objekty.length
            return (
              <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                {struktura.objekty.map(o => (
                  <button key={o} type="button" style={chip(sel.includes(o))} onMouseDown={e => e.preventDefault()}
                    onClick={() => setDraft(formatObjekty(sel.includes(o) ? sel.filter(x => x !== o) : struktura.objekty.filter(x => x === o || sel.includes(x))))}>{o}</button>
                ))}
                <button type="button" style={chip(vsetky)} onMouseDown={e => e.preventDefault()} onClick={() => setDraft(formatObjekty(struktura.objekty))}>Všetky</button>
              </div>
            )
          })()}
          {col.k === 'dni' && row.trvanie === 'm2' && <div style={{ ...hintStyle, whiteSpace: 'nowrap', textAlign: 'right' }}>zadaním dní prepneš na dni</div>}
        </div>
      )
    } else if (badText != null) {
      content = badText
    } else if (col.k === 'partia') {
      const slot = slotOf(harok, row.slot)
      const p = slot && slot.party_id ? byId[slot.party_id] : null
      title = slot ? (slot.party_id ? (bezPartaka(p) ? 'Partia bez parťáka — doplň parťáka v Zamestnancoch' : undefined) : PLANOVANA) : undefined
      content = slot ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, maxWidth: col.w - 16, overflow: 'hidden' }}>
          <span style={{ width: 4, height: 22, borderRadius: 2, flexShrink: 0, background: slot.party_id ? slotFarba(slot, byId) : hatch(0.5, 0.2), border: slot.party_id ? 'none' : `1px dashed ${COLORS.total}` }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', color: COLORS.text }}>{slotMeno(slot, byId)}</span>
          {bezPartaka(p) && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.total, flexShrink: 0 }}>bez parťáka</span>}
        </span>
      ) : <span style={{ color: COLORS.muted }}>{canEdit ? 'vyber partiu' : '—'}</span>
    } else if (col.k === 'usek') content = row.usek
    else if (col.k === 'zahrnut') {
      const z = zahrnutOf(row)
      title = zahrnutText(z) + (canEdit ? ' — B prepne byty, S spoločné priestory' : '')
      content = (
        <span role="group" aria-label="Zahrnúť" style={{ display: 'inline-flex', gap: 4 }}>
          {ZAHRNUT.map((o, oi) => (
            <button key={o.value} type="button" tabIndex={-1} aria-pressed={z.includes(o.value)} disabled={!canEdit}
              onClick={() => toggleZahrnut(i, o.value)}
              style={{ ...chip(z.includes(o.value), editing && (edit.opt || 0) === oi), cursor: canEdit ? 'pointer' : 'default' }}>{o.label}</button>
          ))}
        </span>
      )
    } else if (col.k === 'objekty') content = row.objekty.length ? formatObjekty(row.objekty) : <span style={{ color: COLORS.muted }}>—</span>
    else if (col.k === 'zaciatok') {
      content = row.zaciatok ? formatDatum(row.zaciatok)
        : c.auto ? <span style={{ color: COLORS.muted }} title="Prázdny začiatok: partia pokračuje po svojom predchádzajúcom úseku">{formatDatum(c.start)} <span style={{ fontSize: TYPO.sizes.xs }}>po predch.</span></span>
          : ''
    } else if (col.k === 'dni') {
      const zM2 = row.trvanie === 'm2'
      const slot = slotOf(harok, row.slot)
      const vykon = vykonSlotu(slot)
      let hint = null
      if (zM2) {
        if (c.chyba === 'partia') hint = 'vyber partiu'
        else if (c.chyba === 'm2') hint = 'úsek nemá m²'
        else if (vykon != null) hint = `výkon ${vykonText(vykon)} m²/deň`
        else if (canEdit && slot) hint = <button type="button" tabIndex={-1} style={linkBtn} onClick={() => otvorVykon(slot.slot, c.m2 > 0 && parseInt(row.dni, 10) > 0 ? Math.round(c.m2 / parseInt(row.dni, 10)) : null, row.usek)}>doplň výkon partie</button>
        else hint = 'doplň výkon partie'
      } else if (c.vykon_odhad != null) hint = `≈ ${formatCislo(c.vykon_odhad)} m²/deň`
      const tg = (mode, text) => (
        <button type="button" tabIndex={-1} aria-pressed={(zM2 ? 'm2' : 'dni') === mode} disabled={!canEdit}
          onClick={() => setTrvanie(i, mode)}
          style={{ ...chip((zM2 ? 'm2' : 'dni') === mode), height: 18, lineHeight: '16px', padding: '0 5px', cursor: canEdit ? 'pointer' : 'default' }}>{text}</button>
      )
      title = zM2 ? `Trvanie z m² a výkonu partie za ${vykonZa} — M prepne na dni` : 'Trvanie v dňoch — M prepne na výpočet z m²'
      content = (
        <div style={{ lineHeight: 1.15 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
            <span role="group" aria-label="Trvanie" style={{ display: 'inline-flex', gap: 2 }}>{tg('dni', 'dni')}{tg('m2', 'z m²')}</span>
            <span>{zM2 ? (c.dni_efekt ? c.dni_efekt : '—') : (row.dni === '' ? '' : row.dni)}</span>
          </div>
          {hint && <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 2 }}>{hint}</div>}
        </div>
      )
    } else if (col.k === 'koniec') content = formatDatum(c.end)
    else if (col.k === 'byty') content = bytyText(row)
    else if (col.k === 'm2') {
      const bezPo = objektyBezPoschodia(row, struktura)
      title = (c.m2 != null ? `m² ${zdrojText} (${zahrnutText(zahrnutOf(row)).toLowerCase()})` : (zdrojText ? `Úsek nemá m² ${zdrojText}` : 'Zákazka nemá Výmery'))
        + (bezPo.length ? ` — ${formatObjekty(bezPo)} ${bezPo.length === 1 ? 'nemá' : 'nemajú'} ${poLabel(row.usek)} vo Výmerách, m² sú len z ostatných objektov` : '')
      content = c.m2 == null ? '' : (bezPo.length ? <span style={{ color: COLORS.total }}>{formatM2(c.m2)}</span> : formatM2(c.m2))
    } else if (col.k === 'stav') content = c.stav.kod ? <StavStitok stav={c.stav.kod} text={c.stav.text} title={c.stav.popis || c.stav.text} /> : null
    return (
      <td key={col.k} data-cell={`${i}-${ci}`} tabIndex={-1} style={style} role="gridcell" aria-selected={active} title={title}
        onMouseDown={() => { lastKeyRef.current = ''; if (!editing) setCur({ r: i, c: ci }) }}
        onBlur={editing && col.edit === 'toggle' ? (e => { if (!e.currentTarget.contains(e.relatedTarget) && editRef.current) finishEdit(null, false) }) : undefined}
        onDoubleClick={() => { if (!editing && canEdit && col.edit && col.edit !== 'toggle') startEdit(null, i, ci) }}>
        {content}
      </td>
    )
  }

  const zasahCasti = [
    kpi.bez_partie && `${kpi.bez_partie} bez partie`, kpi.bez_zaciatku && `${kpi.bez_zaciatku} bez začiatku`,
    kpi.bez_vykonu && `${kpi.bez_vykonu} bez výkonu partie`, kpi.bez_m2 && `${kpi.bez_m2} bez m²`,
    kpi.ine_problemy && `${kpi.ine_problemy} bez dní, s chybou alebo prekrytím`,
    kpi.caka_na_predosly && `${kpi.caka_na_predosly} ${kpi.caka_na_predosly >= 2 && kpi.caka_na_predosly <= 4 ? 'čakajú' : 'čaká'} na predošlý úsek`,
  ].filter(Boolean).join(' · ')

  const pSloty = partieOkno ? harok.partie.map(s => ({ s, n: harok.riadky.filter(r => r.slot === s.slot).length })) : []

  return (
    <>
      <div ref={rootRef} tabIndex={-1} onKeyDownCapture={onKeyCapture} onKeyDown={onKeyBubble} onPaste={onPaste}
        style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md, padding: `${SPACE.sm}px 0`, outline: 'none' }}>

        {/* potery_rekap_v1 (Mirko 17. 10. 2026, F2): skutocne tempo zo Vymer — IBA ZOBRAZENIE, plan sa podla neho nemeni */}
        {remeslo === 'potery' && tempo && tempo.pokladka && (() => {
          const c = n => (Number(n) || 0).toLocaleString('sk-SK', { maximumFractionDigits: 1 })
          const dt = x => x ? x.slice(8, 10) + '. ' + x.slice(5, 7) + '. ' + x.slice(0, 4) : ''
          const t = (n, x) => x.malo_dat ? `${n}: málo dát (${x.dni_prace} ${x.dni_prace === 1 ? 'deň' : 'dni'} s prácou, treba aspoň 3)`
            : `${n} ${c(x.tempo_m2_den)} m²/deň · zostáva ${c(x.zostava_m2)} m²` + (x.dni_do_konca != null ? ` · odhad ${x.dni_do_konca} prac. dní, ${dt(x.odhad_dokoncenia)}` : '')
          return <div title="Skutočné tempo z potvrdených miestností vo Výmerách (podľa dňa zaškrtnutia na stavbe). Iba informácia — plán upravuješ ty."
            style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, borderLeft: `3px solid ${COLORS.nav}`, borderRadius: RADIUS.lg, padding: `${SPACE.xs}px ${SPACE.md}px` }}>
            <b style={{ color: COLORS.text }}>Skutočné tempo zo Výmer</b> · {t('pokládka', tempo.pokladka)} · {t('anhydrit', tempo.anhydrit)} · <i>odhad, plán sa nemení</i>
          </div>
        })()}

        {/* ── lišta ── */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: SPACE.lg, flexWrap: 'wrap' }}>
          <div>
            <span style={lbl}>Termín</span>
            <input value={terminDraft !== null ? terminDraft : formatDatum(termin)} disabled={!canEdit} placeholder="dd.mm.rrrr" aria-label="Termín dokončenia"
              onChange={e => { setTerminDraft(e.target.value); setTerminBad(false) }}
              onBlur={commitTermin}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitTermin() } else if (e.key === 'Escape') { e.preventDefault(); setTerminDraft(null); setTerminBad(false) } }}
              style={{ ...inputBase, width: 120, borderColor: terminBad ? COLORS.remove : CELL.input.border, background: terminBad ? COLORS.removeBg : (canEdit ? CELL.input.bg : COLORS.bgSoft) }} />
          </div>
          <div>
            <span style={lbl}>Počítať dni</span>
            <div style={{ display: 'flex', gap: SPACE.xs }}>
              <Btn variant={harok.dni === 'kal' ? 'nav' : 'ghost'} disabled={!canEdit && harok.dni !== 'kal'} onClick={() => canEdit && setRezimDni('kal')}>Kalendárne dni</Btn>
              <Btn variant={harok.dni === 'prac' ? 'nav' : 'ghost'} disabled={!canEdit && harok.dni !== 'prac'} onClick={() => canEdit && setRezimDni('prac')}>Pracovné dni Po–Pi</Btn>
            </div>
          </div>
          {canWrite && <BtnNav onClick={addRow} disabled={!canEdit} title="Nový úsek na koniec hárku; v hárku Alt+N pridá úsek pod označený riadok">Pridať úsek (Alt+N)</BtnNav>}
          <BtnGhost onClick={() => otvorPartie()} title="Farba, názov a výkon partií hárka, plánované partie a nahradenie skutočnou partiou">Partie a výkon</BtnGhost>
          <BtnGhost onClick={openVerzie}>Verzie</BtnGhost>
          <BtnGhost onClick={stiahniPdf} disabled={pdfBusy || neulozene} title={neulozene ? 'Najprv počkaj na „Uložené“' : 'PDF na hlavičkovom papieri KAMIR, A4 na šírku'}>{pdfBusy ? 'Vytváram PDF…' : 'PDF'}</BtnGhost>
          {canWrite && <BtnGhost onClick={otvorOdoslat} disabled={neulozene} title={neulozene ? 'Najprv počkaj na „Uložené“' : 'Odoslať harmonogram e-mailom s PDF v prílohe'}>Odoslať</BtnGhost>}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: TYPO.sizes.xs, color: saveState.kod === 'error' ? COLORS.remove : COLORS.muted, fontVariantNumeric: 'tabular-nums' }}>{saveText}</span>
        </div>

        {conflict && (
          <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: SPACE.md, flexWrap: 'wrap', padding: '8px 12px', background: COLORS.totalBg, border: `1px solid ${COLORS.total}`, borderRadius: RADIUS.md, color: COLORS.total, fontSize: TYPO.sizes.sm }}>
            <span style={{ flex: 1 }}>Harmonogram medzitým zmenil <b>{meno(conflict.kto) || 'iný používateľ'}</b>{conflict.kedy ? ' (' + fmtCas(conflict.kedy) + ')' : ''}. Tvoje posledné zmeny sa neuložili.</span>
            <BtnNav size="sm" onClick={nacitatZnova}>Načítať znova</BtnNav>
          </div>
        )}
        {!serverOk && (
          <div role="alert" style={{ padding: '8px 12px', background: COLORS.removeBg, border: `1px solid ${COLORS.remove}`, borderRadius: RADIUS.md, color: COLORS.remove, fontSize: TYPO.sizes.sm }}>
            Server ešte nepozná harmonogram po remeslách — hárok je len na čítanie, aby sa neprepísal plán omietok.
          </div>
        )}
        {!canWrite && <div style={hintStyle}>Harmonogram môžeš len prezerať.</div>}

        {/* ── KPI (92.6) ── */}
        <KpiRow cols={4}>
          <KpiCard title="Koniec prác" color={!kpi.koniec ? COLORS.total : (termin && kpi.koniec > termin ? COLORS.remove : (kpi.neuplny ? COLORS.total : COLORS.add))}>
            <div style={kpiVal}>{kpi.koniec ? formatDatum(kpi.koniec) : '—'}</div>
            <div style={kpiSub}>{kpi.koniec ? (kpi.neuplny ? 'plán je neúplný · ' : '') + 'posledný úsek: ' + (kpi.posledny_usek || 'bez názvu') : (kpi.pocet ? 'doplň začiatok a trvanie' : 'pridaj prvý úsek')}</div>
          </KpiCard>
          {rez != null && rez < 0 ? (
            <KpiCard title="Meškanie za termínom" color={COLORS.remove}>
              <div style={kpiVal}>{-rez} {dniText(rez)}</div>
              <div style={kpiSub}>termín {formatDatum(termin)}{kpi.neuplny ? ' · plán je neúplný' : ''}</div>
            </KpiCard>
          ) : (
            <KpiCard title="Rezerva do termínu" color={rez == null ? COLORS.muted : COLORS.total}>
              <div style={kpiVal}>{rez == null ? '—' : rez + ' ' + dniText(rez)}</div>
              <div style={kpiSub}>{(termin ? 'termín ' + formatDatum(termin) : 'termín nezadaný') + (kpi.neuplny && rez != null ? ' · plán je neúplný' : '')}</div>
            </KpiCard>
          )}
          <KpiCard title="Úseky" color={COLORS.nav}>
            <div style={kpiVal}>{kpi.pocet ? `${kpi.hotove} hotové z ${kpi.pocet}` : '—'}</div>
            {kpi.m2_spolu > 0 && <div style={kpiSub} title={zdrojText ? 'm² ' + zdrojText : undefined}>m² spolu {formatCislo(kpi.m2_spolu)}</div>}
            <div style={kpiSub}>{kpi.pocet ? `${kpi.prebieha} prebieha, ${kpi.ostatne} ďalších` : 'zatiaľ žiadny úsek'}</div>
          </KpiCard>
          <KpiCard title="Treba zásah" color={kpi.treba_zasah ? COLORS.total : COLORS.add}>
            <div style={kpiVal}>{kpi.treba_zasah ? kpi.treba_zasah + ' ' + usekyText(kpi.treba_zasah) : 'Nič'}</div>
            <div style={kpiSub}>{kpi.treba_zasah ? zasahCasti : 'každý úsek má partiu, začiatok aj trvanie'}</div>
            {kpi.planovane_useky > 0 && <div style={kpiSub}>plánované partie: {kpi.planovane_useky} {usekyText(kpi.planovane_useky)}</div>}
          </KpiCard>
        </KpiRow>

        {/* ── hárok ── */}
        <div style={hintStyle}>
          Vyplň ako v Exceli. Prázdny začiatok znamená, že partia pokračuje hneď po svojom predchádzajúcom úseku.
          V stĺpci Zahrnúť prepína B byty a S spoločné priestory (obe = celé poschodie); v stĺpci Trvanie M prepne dni a výpočet z m² podľa výkonu partie za {vykonZa}.
          Blok z Excelu vložíš cez {MOD_KEY}+V v poradí Partia, Úsek, Objekty, Začiatok, Dni, voliteľne Zahrnúť (byty, spoločné, celé).
          {zdrojText ? ` Počet bytov a m² sú ${zdrojText}.` : ''}
        </div>
        <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, background: COLORS.bg, overflow: 'hidden' }}>
          <div ref={gridRef}
            onFocus={() => setGridFocus(true)}
            onBlur={e => { if (!gridRef.current || !gridRef.current.contains(e.relatedTarget)) setGridFocus(false) }}
            style={{ overflowX: 'auto' }}>
            <table role="grid" aria-label="Hárok úsekov" aria-rowcount={nRows + 1} style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: COLS.reduce((s, col) => s + col.w, 0) + AKCIE_W, fontSize: TYPO.sizes.base, color: COLORS.text }}>
              <thead>
                <tr role="row" aria-rowindex={1}>
                  {COLS.map(col => (
                    <th key={col.k} role="columnheader" title={col.k === 'm2' && zdrojText ? 'm² ' + zdrojText : (col.k === 'dni' ? `Trvanie v dňoch alebo z m² a výkonu partie za ${vykonZa}` : undefined)}
                      style={{ ...thS, width: col.w, textAlign: col.num ? 'right' : 'left', ...(col.k === 'stav' ? { position: 'sticky', right: AKCIE_W, zIndex: 2, background: hlavickaBg, boxShadow: `inset 1px 0 0 ${COLORS.line}` } : {}) }}>{col.t}</th>
                  ))}
                  <th role="columnheader" style={{ ...thS, width: AKCIE_W, position: 'sticky', right: 0, zIndex: 2, background: hlavickaBg }}><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Akcie</span></th>
                </tr>
              </thead>
              <tbody>
                {harok.riadky.map((row, i) => (
                  <tr key={row.id} role="row" aria-rowindex={i + 2} style={{ background: i % 2 ? CELL.zebra.bg : COLORS.bg }}>
                    {COLS.map((col, ci) => renderCell(row, i, col, ci))}
                    <td role="gridcell" style={{ padding: '2px 4px', borderBottom: `1px solid ${COLORS.lineSoft}`, whiteSpace: 'nowrap', textAlign: 'right', width: AKCIE_W, position: 'sticky', right: 0, zIndex: 1, background: plneBg(i) }}>
                      {canWrite && (
                        <span style={{ display: 'inline-flex', gap: 2 }}>
                          <Btn size="sm" variant="ghost" disabled={!canEdit} aria-pressed={!!row.hotovo} onClick={() => toggleHotovo(i)}
                            style={{ borderColor: 'transparent', padding: '4px 6px', color: row.hotovo ? COLORS.add : COLORS.muted, ...(row.hotovo ? { background: COLORS.addBg } : {}) }}
                            title={row.hotovo ? 'Úsek je označený ako hotový — klik označenie zruší' : 'Označiť úsek ako hotový aj pred koncom'}>Hotové</Btn>
                          <Btn size="sm" variant="ghost" disabled={!canEdit} onClick={() => removeRow(i)} style={{ borderColor: 'transparent', padding: '4px 6px', color: COLORS.remove }}>Odstrániť</Btn>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListStatusBar>
            <span aria-live="polite" style={{ fontVariantNumeric: 'tabular-nums' }}>
              <b style={{ color: edit ? COLORS.total : COLORS.nav }}>{edit ? 'Úprava' : 'Pohyb'}</b>
              {' · '}{COLS[cur.c] ? COLS[cur.c].t : ''} · riadok {Math.min(cur.r + 1, nRows)} z {nRows}{rowNow && rowNow.usek ? ` (${rowNow.usek})` : ''}
            </span>
            <span style={{ marginLeft: SPACE.lg }}>Šípky a Tab pohyb · Enter alebo F2 úprava · Esc zruší · Delete vymaže bunku · F8 z riadku vyššie · B/S v Zahrnúť byty/spoločné · M trvanie z m² · Alt+N nový úsek · {MOD_KEY}+Z späť · {MOD_KEY}+V z Excelu</span>
          </ListStatusBar>
        </div>

        {/* ── Gantt po dňoch ── */}
        <HarokGantt harok={harok} rows={rows} byId={byId} today={today} />
      </div>

      {/* ── modaly mimo kontajnera klávesnice (Esc modalu ide cez document) ── */}
      <Modal open={!!vlozit} onClose={() => { setVlozit(null); vratFokus() }} title="Vložiť z Excelu" width="min(980px, 95vw)"
        subtitle="Riadky sa vložia pod označený riadok. Meno, ktoré nie je medzi partiami, sa vloží ako plánovaná partia s týmto menom. Prázdna alebo nejednoznačná partia ostane bez partie a vyberieš ju v hárku. Chybné bunky ostanú označené a neuložia sa. Bez stĺpca Zahrnúť sa berie celé poschodie."
        footer={vlozit && <>
          <BtnGhost onClick={() => { setVlozit(null); vratFokus() }}>Zrušiť</BtnGhost>
          <BtnNav onClick={vlozitPotvrdit}>Vložiť {vlozit.rows.length} {vlozit.rows.length === 1 ? 'riadok' : (vlozit.rows.length < 5 ? 'riadky' : 'riadkov')}</BtnNav>
        </>}>
        {vlozit && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: TYPO.sizes.sm }}>
              <thead><tr>{['Partia', 'Úsek', 'Objekty', 'Začiatok', 'Dni', 'Zahrnúť'].map(t => <th key={t} style={thS}>{t}</th>)}</tr></thead>
              <tbody>
                {vlozit.rows.map((x, i) => {
                  const err = { color: COLORS.remove, background: COLORS.removeBg }
                  const td = { padding: '5px 8px', borderBottom: `1px solid ${COLORS.lineSoft}`, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }
                  const p = x.partia && x.partia.party_id ? (byId[x.partia.party_id] || x.partia) : null
                  return (
                    <tr key={i}>
                      <td style={td}>
                        {p ? (
                          <span>{partiaMeno(p)}{bezPartaka(p) && <> <StavStitok stav="zasah" text="bez parťáka" /></>}{x.ina_zhoda && <span style={{ color: COLORS.muted }}> (zapísané „{x.partia_text}“)</span>}</span>
                        ) : x.planovana ? (
                          <span>{x.nazov_vlozenia} <StavStitok stav="neutralne" text={x.sparovana ? 'plánovaná partia v hárku' : 'nová plánovaná partia'} /></span>
                        ) : (
                          <span>{x.partia_text && <span style={{ color: COLORS.muted }}>{x.partia_text} </span>}<StavStitok stav="zasah" text={x.nejednoznacna ? `nejednoznačná (${x.nejednoznacna} ${x.nejednoznacna < 5 ? 'partie' : 'partií'}) — bez partie` : 'bez partie'} /></span>
                        )}
                      </td>
                      <td style={td}>{x.usek}</td>
                      <td style={{ ...td, ...(x.objekty_chyba ? err : {}) }}>{x.objekty_chyba ? x.objekty_text : formatObjekty(x.objekty)}</td>
                      <td style={{ ...td, ...(x.zaciatok_chyba ? err : {}) }}>{x.zaciatok_chyba ? x.zaciatok_text : (x.zaciatok ? formatDatum(x.zaciatok) : <span style={{ color: COLORS.muted }}>po predchádzajúcom</span>)}</td>
                      <td style={{ ...td, textAlign: 'right', ...(x.dni_chyba ? err : {}) }}>{x.dni_chyba ? x.dni_text : x.dni}</td>
                      <td style={{ ...td, ...(x.zahrnut_chyba ? err : {}) }} title={x.zahrnut_chyba ? 'Neznáma hodnota — vloží sa celé poschodie' : undefined}>{x.zahrnut_chyba ? `${x.zahrnut_text} → celé poschodie` : zahrnutText(x.zahrnut)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Modal open={!!partieOkno} onClose={zavrietPartie} title="Partie a výkon" width="min(1000px, 95vw)"
        subtitle={`Výkon je v m² za ${vykonZa} a platí pre úseky s trvaním z m². Plánovaná partia je partia, s ktorou rátaš, ale ešte ju nemáš — neskôr ju nahradíš skutočnou partiou a úseky aj dni ostanú.`}
        footer={partieOkno && <>
          {canEdit && <BtnNav onClick={pridatPlanovanu}>Pridať plánovanú partiu</BtnNav>}
          <BtnGhost onClick={zavrietPartie}>Zavrieť</BtnGhost>
        </>}>
        {partieOkno && (!pSloty.length ? (
          <div style={{ color: COLORS.muted }}>V hárku zatiaľ nie je žiadna partia. Partiu vyberieš v stĺpci Partia alebo pridáš plánovanú partiu.</div>
        ) : (
          <div style={{ overflowX: 'auto', minHeight: partieOkno.nahradit ? 300 : undefined }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: TYPO.sizes.sm }}>
              <thead><tr>
                <th style={{ ...thS, width: 28 }}><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Farba</span></th>
                <th style={thS}>Názov</th><th style={thS}>Druh</th>
                <th style={{ ...thS, textAlign: 'right' }} title={`m² za ${vykonZa}`}>Výkon m²/deň</th>
                <th style={{ ...thS, textAlign: 'right' }}>Úsekov</th><th style={thS} />
              </tr></thead>
              <tbody>
                {pSloty.map(({ s, n }, i) => {
                  const td = { padding: '5px 8px', borderBottom: `1px solid ${COLORS.lineSoft}`, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle', background: i % 2 ? CELL.zebra.bg : COLORS.bg }
                  const plan = !s.party_id
                  const p = plan ? null : byId[s.party_id]
                  const kN = s.slot + '|nazov', kV = s.slot + '|vykon'
                  const fokusRef = (pole) => (el) => {
                    if (!el || partieOkno.fokus !== s.slot || partieOkno.pole !== pole || pFokusRef.current === s.slot + pole) return
                    pFokusRef.current = s.slot + pole
                    // Modal pri otvorení dá fokus prvému prvku — až potom
                    setTimeout(() => { try { el.focus(); el.select() } catch (_) {} }, 0)
                  }
                  const inp = (k, value, extra) => ({
                    value, disabled: !canEdit,
                    onChange: e => { const v = e.target.value; setPDraft(d => ({ ...d, [k]: v })); setPBad(b => { if (!(k in b)) return b; const x = { ...b }; delete x[k]; return x }) },
                    onKeyDown: e => {
                      if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() }
                      else if (e.key === 'Escape' && k in pDraft) { e.preventDefault(); e.stopPropagation(); dropPDraft(k) }
                    },
                    style: { ...inputBase, padding: '4px 6px', borderColor: pBad[k] ? COLORS.remove : CELL.input.border, background: pBad[k] ? COLORS.removeBg : (canEdit ? CELL.input.bg : COLORS.bgSoft), ...extra },
                  })
                  const nah = partieOkno.nahradit && partieOkno.nahradit.slot === s.slot ? partieOkno.nahradit : null
                  return (
                    <React.Fragment key={s.slot}>
                      <tr>
                        <td style={td}><span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, verticalAlign: 'middle', background: plan ? hatch(0.5, 0.2) : slotFarba(s, byId), border: plan ? `1px dashed ${COLORS.total}` : 'none' }} /></td>
                        <td style={td}>
                          {plan
                            ? <input ref={fokusRef('nazov')} aria-label="Názov plánovanej partie" {...inp(kN, kN in pDraft ? pDraft[kN] : nazovPlanovanej(s), { width: 180 })} onBlur={() => { if (kN in pDraft) commitSlotNazov(s.slot, pDraft[kN]) }} />
                            : <span>{slotMeno(s, byId)}</span>}
                        </td>
                        <td style={td}>
                          {plan ? <StavStitok stav="neutralne" text={PLANOVANA} /> : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><StavStitok stav="neutralne" text="Skutočná partia" />{bezPartaka(p) && <StavStitok stav="zasah" text="bez parťáka" title="Doplň parťáka v Zamestnancoch" />}</span>}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <input ref={fokusRef('vykon')} aria-label={`Výkon ${slotMeno(s, byId)} v m² za ${vykonZa}`} placeholder="—" inputMode="decimal"
                            {...inp(kV, kV in pDraft ? pDraft[kV] : (vykonSlotu(s) == null ? '' : String(vykonSlotu(s)).replace('.', ',')), { width: 90, textAlign: 'right' })}
                            onBlur={() => { if (kV in pDraft) commitSlotVykon(s.slot, pDraft[kV]) }} />
                          {partieOkno.navrh && partieOkno.navrh.slot === s.slot && pDraft[kV] === partieOkno.navrh.text && (
                            <div style={{ ...hintStyle, whiteSpace: 'nowrap' }}>návrh z dní úseku{partieOkno.navrh.usek ? ' ' + partieOkno.navrh.usek : ''} — Enter potvrdí, Esc zruší</div>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>{n}</td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {canEdit && (
                            <span style={{ display: 'inline-flex', gap: SPACE.xs }}>
                              <BtnGhost size="sm" onClick={() => setPartieOkno(o => ({ ...o, nahradit: nah ? null : { slot: s.slot, party: '' } }))}>{plan ? 'Nahradiť skutočnou partiou' : 'Nahradiť inou partiou'}</BtnGhost>
                              {n === 0 && <Btn size="sm" variant="ghost" onClick={() => odstranitSlot(s.slot)} style={{ color: COLORS.remove }}>Odstrániť</Btn>}
                            </span>
                          )}
                        </td>
                      </tr>
                      {nah && (
                        <tr>
                          <td style={td} />
                          <td colSpan={5} style={{ ...td, whiteSpace: 'normal' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: SPACE.md, flexWrap: 'wrap' }}>
                              <div style={{ minWidth: 320, flex: 1 }}>
                                <span style={lbl}>Nahradiť partiou</span>
                                <SearchableSelect value={nah.party} options={optsNahradit} menuPortal placeholder="Hľadať partiu…"
                                  onChange={v => { if (String(v).startsWith('party:')) setPartieOkno(o => ({ ...o, nahradit: { ...o.nahradit, party: v } })) }} />
                              </div>
                              <BtnNav size="sm" disabled={!String(nah.party || '').startsWith('party:')} onClick={nahraditPotvrdit}>Nahradiť</BtnNav>
                              <BtnGhost size="sm" onClick={() => setPartieOkno(o => ({ ...o, nahradit: null }))}>Zrušiť</BtnGhost>
                            </div>
                            <div style={{ ...hintStyle, marginTop: 4 }}>Úseky, začiatky, trvanie aj výkon ostanú, zmení sa len partia. Partia bez parťáka sa dá vybrať, parťáka doplň v Zamestnancoch.</div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        ))}
      </Modal>

      <Modal open={!!odoslat} onClose={() => { if (!(odoslat && odoslat.busy)) { setOdoslat(null); vratFokus() } }} title="Odoslať harmonogram" width="min(620px, 95vw)">
        {odoslat && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
            <div style={hintStyle}>Odíde e-mail s PDF harmonogramu (uložená verzia {revRef.current}) zo schránky Matrixu. Kópia ide aj do odoslane@kamir.sk a zápis do vlákna zákazky.</div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: TYPO.sizes.sm }}>Komu *
              <input type="email" value={odoslat.email} onChange={e => setOdoslat(o => ({ ...o, email: e.target.value }))} autoFocus
                style={{ padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm, fontFamily: 'inherit' }} /></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: TYPO.sizes.sm }}>Kópia (voliteľné, viac adries oddeľ čiarkou)
              <input type="text" value={odoslat.cc} onChange={e => setOdoslat(o => ({ ...o, cc: e.target.value }))}
                style={{ padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm, fontFamily: 'inherit' }} /></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: TYPO.sizes.sm }}>Predmet
              <input type="text" value={odoslat.predmet} onChange={e => setOdoslat(o => ({ ...o, predmet: e.target.value }))}
                style={{ padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm, fontFamily: 'inherit' }} /></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: TYPO.sizes.sm }}>Správa
              <textarea value={odoslat.text} onChange={e => setOdoslat(o => ({ ...o, text: e.target.value }))} rows={6}
                style={{ padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm, fontFamily: 'inherit', resize: 'vertical' }} /></label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: TYPO.sizes.sm }}>
              <input type="checkbox" checked={!!odoslat.sms} onChange={e => setOdoslat(o => ({ ...o, sms: e.target.checked }))} /> Poslať aj informačnú SMS zákazníkovi</label>
            <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
              <BtnGhost onClick={() => { setOdoslat(null); vratFokus() }} disabled={odoslat.busy}>Zrušiť</BtnGhost>
              <BtnNav onClick={odoslatHarmonogram} disabled={odoslat.busy || !/^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(String(odoslat.email).trim())}>{odoslat.busy ? 'Odosielam…' : 'Odoslať'}</BtnNav>
            </div>
          </div>
        )}
      </Modal>
      <Modal open={!!verzie} onClose={() => { setVerzie(null); vratFokus() }} title="Verzie hárku" width="min(760px, 95vw)"
        subtitle="Verzia je stav hárku pred uložením. Matrix ho odloží pred každou väčšou zmenou, keď hárok prepisuje iný používateľ, po prestávke a inak najviac raz za 10 minút.">
        {verzie && <div style={{ ...hintStyle, marginBottom: SPACE.sm }}>Aktuálny stav: revízia {revRef.current != null ? revRef.current : '—'}{saveState.cas ? `, uložené ${saveState.cas}` : ''}{saveState.kod !== 'ok' ? ' · ' + saveText : ''}</div>}
        {verzie && (verzie.loading ? <div style={{ color: COLORS.muted }}>Načítavam verzie…</div>
          : verzie.err ? <div style={{ color: COLORS.remove }}>Verzie sa nepodarilo načítať: {verzie.err}</div>
            : !verzie.list.length ? <div style={{ color: COLORS.muted }}>Zatiaľ žiadne uložené verzie.</div>
              : (
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: TYPO.sizes.sm }}>
                  <thead><tr><th style={thS}>Stav pred uložením</th><th style={thS}>Uložil</th><th style={thS}>Dôvod</th><th style={{ ...thS, textAlign: 'right' }}>Revízia</th><th style={thS} /></tr></thead>
                  <tbody>
                    {verzie.list.map((v, i) => {
                      const td = { padding: '5px 8px', borderBottom: `1px solid ${COLORS.lineSoft}`, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', background: i % 2 ? CELL.zebra.bg : COLORS.bg }
                      return (
                        <tr key={v.id}>
                          <td style={td}>{fmtCas(v.saved_at)}</td>
                          <td style={td}>{meno(v.saved_by) || '—'}</td>
                          <td style={td}>{DOVODY[v.dovod] || 'Priebežné uloženie'}{v.trvaly ? ' · trvalá' : ''}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{v.rev}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{canWrite && <BtnNav size="sm" disabled={!canEdit} onClick={() => obnovit(v)}>Obnoviť</BtnNav>}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              ))}
      </Modal>
    </>
  )
}

// Gantt po dňoch podľa schváleného náhľadu: 22 px na deň, víkendy, čiara Dnes a Termín, šrafovaná rezerva.
export function HarokGantt({ harok, rows, byId, today }) {
  const starts = rows.filter(c => c.start).map(c => c.start)
  const card = { border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, background: COLORS.bg }
  const legendItem = (sw, text, key) => (
    <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{sw}{text}</span>
  )
  if (!starts.length) {
    return <div style={{ ...card, padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Gantt sa zobrazí po doplnení aspoň jedného začiatku a trvania.</div>
  }
  const ends = rows.filter(c => c.end).map(c => c.end)
  const termin = harok.termin || ''
  const min = starts.reduce((a, b) => (a < b ? a : b))
  const max = [...ends, today, termin].filter(Boolean).reduce((a, b) => (a > b ? a : b))
  const from = mondayOf(min)
  let to = addCalDays(max, 4)
  let n = diffDays(from, to) + 1
  const skrateny = n > GANTT_MAX_DNI
  if (skrateny) { n = GANTT_MAX_DNI; to = addCalDays(from, n - 1) }
  const W = n * DW
  const x = (d) => diffDays(from, d) * DW
  const days = Array.from({ length: n }, (_, i) => addCalDays(from, i))
  const koniec = ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : null

  const usedSlots = []
  harok.riadky.forEach(r => { if (!usedSlots.includes(r.slot)) usedSlots.push(r.slot) })

  const labelS = { position: 'sticky', left: 0, zIndex: 3, width: LW, flex: '0 0 auto', background: COLORS.bg, borderRight: `1px solid ${COLORS.line}`, boxSizing: 'border-box' }
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: `${SPACE.xs}px ${SPACE.lg}px`, flexWrap: 'wrap', padding: `${SPACE.sm}px ${SPACE.md}px`, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
        <b style={{ color: COLORS.text, fontSize: TYPO.sizes.sm, marginRight: SPACE.sm }}>Harmonogram</b>
        {usedSlots.map(sid => {
          const slot = slotOf(harok, sid)
          const neo = !slot || !slot.party_id
          const sw = <i style={{ display: 'inline-block', width: 18, height: 10, borderRadius: 3, background: neo ? hatch(0.5, 0.15) : slotFarba(slot, byId), border: neo ? `1px dashed ${COLORS.total}` : 'none' }} />
          return legendItem(sw, slot ? slotMeno(slot, byId) : 'Bez partie', 's' + sid)
        })}
        {legendItem(<i style={{ display: 'inline-block', width: 2, height: 14, background: COLORS.nav }} />, 'Dnes', 'dnes')}
        {termin && legendItem(<i style={{ display: 'inline-block', width: 2, height: 14, background: COLORS.remove }} />, 'Termín', 'termin')}
        {termin && legendItem(<i style={{ display: 'inline-block', width: 18, height: 10, borderRadius: 3, background: hatch(0.35, 0.1) }} />, 'Rezerva', 'rezerva')}
        {skrateny && <span role="alert" style={{ color: COLORS.remove, fontWeight: TYPO.weights.semibold }}>Gantt je skrátený na {GANTT_MAX_DNI} dní — skontroluj dátumy a trvanie</span>}
      </div>
      <div style={{ overflowX: 'auto', borderTop: `1px solid ${COLORS.line}` }}>
        <div style={{ width: LW + W }}>
          <div style={{ display: 'flex' }}>
            <div style={{ ...labelS, height: 26, background: `linear-gradient(${CELL.header.bg}, ${CELL.header.bg}), ${COLORS.bg}`, color: CELL.header.text, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, display: 'flex', alignItems: 'center', padding: '0 8px' }}>Mesiac</div>
            <div style={{ position: 'relative', width: W, height: 26, background: CELL.header.bg, borderBottom: `1px solid ${COLORS.lineSoft}` }}>
              {days.map((d, i) => (d.slice(8, 10) === '01' || i === 0) ? (
                <div key={d} style={{ position: 'absolute', left: i * DW, top: 0, bottom: 0, padding: '5px 6px', borderLeft: `1px solid ${COLORS.line}`, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.text, whiteSpace: 'nowrap' }}>
                  {MESIACE[parseInt(d.slice(5, 7), 10) - 1]} {d.slice(0, 4)}
                </div>
              ) : null)}
            </div>
          </div>
          <div style={{ display: 'flex' }}>
            <div style={{ ...labelS, height: 22, background: `linear-gradient(${CELL.header.bg}, ${CELL.header.bg}), ${COLORS.bg}`, color: CELL.header.text, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, display: 'flex', alignItems: 'center', padding: '0 8px' }}>Partia a úsek</div>
            <div style={{ position: 'relative', width: W, height: 22, background: CELL.header.bg, borderBottom: `1px solid ${COLORS.line}` }}>
              {days.map((d, i) => {
                const we = isWeekendIso(d)
                return <div key={d} style={{ position: 'absolute', left: i * DW, width: DW, top: 0, bottom: 0, paddingTop: 4, textAlign: 'center', fontSize: TYPO.sizes.xs, fontVariantNumeric: 'tabular-nums', color: we ? COLORS.nav : COLORS.text, background: we ? TINT.nav : 'transparent' }}>{parseInt(d.slice(8, 10), 10)}</div>
              })}
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            {days.map((d, i) => isWeekendIso(d) ? <div key={'we' + d} style={{ position: 'absolute', left: LW + i * DW, width: DW, top: 0, bottom: 0, background: TINT.nav }} /> : null)}
            {harok.riadky.map((r, i) => {
              const c = rows[i]
              const slot = slotOf(harok, r.slot)
              const neo = !slot || !slot.party_id
              const color = slotFarba(slot, byId)
              let bar = null
              if (c && c.start && c.end && c.start <= to) {
                const left = x(c.start)
                const w = (diffDays(c.start, c.end > to ? to : c.end) + 1) * DW
                const kod = c.stav.kod
                const st = { position: 'absolute', left, width: w, top: 6, height: RH - 12, borderRadius: 5, display: 'flex', alignItems: 'center', padding: '0 8px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, whiteSpace: 'nowrap', overflow: 'hidden', boxSizing: 'border-box' }
                // text na plnej farbe partie: tmavý alebo biely podľa kontrastu (svetlé farby partií by s bielou boli nečitateľné)
                const naFarbe = textNaFarbe(color, COLORS.text, COLORS.bg)
                if (neo) { st.background = hatch(0.22, 0.08); st.border = `1px dashed ${COLORS.total}`; st.color = COLORS.total }
                else if (kod === 'hotovo') { st.background = color; st.color = naFarbe }
                else if (kod === 'prebieha') {
                  const pct = Math.max(0, Math.min(100, Math.round((diffDays(c.start, today) + 0.5) / (diffDays(c.start, c.end) + 1) * 100)))
                  st.background = `linear-gradient(90deg, ${color} 0 ${pct}%, ${tint(color, 0.35)} ${pct}% 100%)`
                  st.color = pct >= 50 ? naFarbe : COLORS.text
                } else {
                  st.background = tint(color, 0.2); st.borderLeft = `3px solid ${color}`; st.color = COLORS.text
                  if (kod === 'chyba') st.boxShadow = `inset 0 0 0 1px ${COLORS.remove}`
                }
                const title = `${r.usek || 'Úsek'} (${zahrnutText(zahrnutOf(r)).toLowerCase()}${c.m2 != null ? ', ' + formatM2(c.m2) + ' m²' : ''}): ${formatDatum(c.start)} až ${formatDatum(c.end)}, ${c.dni} ${dniText(c.dni)}${r.trvanie === 'm2' ? ' z m²' : ''} · ${c.stav.text}`
                bar = (
                  <div title={title} role="img" aria-label={(slot ? slotMeno(slot, byId) + (neo ? ' (plánovaná)' : '') + ' · ' : '') + title} style={st}>
                    {r.usek || 'Úsek'}
                    {w > 150 && <span style={{ fontWeight: TYPO.weights.normal, opacity: 0.85, marginLeft: 6 }}>{formatDM(c.start)}–{formatDM(c.end)}</span>}
                    {neo && w > 240 && <span style={{ fontWeight: TYPO.weights.normal, marginLeft: 6 }}>plánovaná</span>}
                  </div>
                )
              }
              return (
                <div key={r.id} style={{ display: 'flex' }}>
                  <div style={{ ...labelS, height: RH, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px 0 8px', borderBottom: `1px solid ${COLORS.lineSoft}` }}>
                    <span style={{ width: 4, height: 24, borderRadius: 2, flexShrink: 0, background: neo ? hatch(0.5, 0.2) : color }} />
                    <span style={{ minWidth: 0, overflow: 'hidden' }}>
                      <b style={{ display: 'block', fontSize: TYPO.sizes.sm, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(r.usek || 'Úsek') + ' · ' + zahrnutText(zahrnutOf(r))}</b>
                      <span style={{ display: 'block', fontSize: TYPO.sizes.xs, color: COLORS.muted, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{slot ? slotMeno(slot, byId) : 'bez partie'}</span>
                    </span>
                  </div>
                  <div style={{ position: 'relative', width: W, height: RH, borderBottom: `1px solid ${COLORS.lineSoft}` }}>{bar}</div>
                </div>
              )
            })}
            {koniec && termin && koniec < termin && koniec < to && (
              <div style={{ position: 'absolute', top: 0, bottom: 0, zIndex: 1, pointerEvents: 'none', left: LW + x(addCalDays(koniec, 1)), width: diffDays(koniec, termin > to ? to : termin) * DW, background: hatch(0.16, 0.05) }} />
            )}
            {today >= from && today <= to && (
              <div style={{ position: 'absolute', top: 0, bottom: 0, width: 2, zIndex: 2, pointerEvents: 'none', left: LW + x(today) + DW / 2 - 1, background: COLORS.nav }}>
                <span style={{ position: 'absolute', top: 2, left: 4, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, whiteSpace: 'nowrap', background: COLORS.bg, color: COLORS.nav, padding: '0 4px', borderRadius: 3 }}>Dnes {formatDatum(today)}</span>
              </div>
            )}
            {termin && termin >= from && termin <= to && (
              <div style={{ position: 'absolute', top: 0, bottom: 0, width: 2, zIndex: 2, pointerEvents: 'none', left: LW + x(termin) + DW - 1, background: COLORS.remove }}>
                <span style={{ position: 'absolute', top: 2, right: 6, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, whiteSpace: 'nowrap', background: COLORS.bg, color: COLORS.remove, padding: '0 4px', borderRadius: 3 }}>Termín {formatDatum(termin)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
